import React, { useState, useEffect, useCallback, useMemo, createContext, useContext } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { api } from './services/api';

/* ─── Auth Context ──────────────────────────────────────────────────── */
const AuthContext = createContext(null);
function useAuth() { return useContext(AuthContext); }

/* ─── Constants ──────────────────────────────────────────────────── */
const STAGES = [
  { key: 'new_lead', label: 'New Lead', color: '#6B7280' },
  { key: 'initial_outreach', label: 'Initial Outreach', color: '#3B82F6' },
  { key: 'info_requested', label: 'Info Requested', color: '#8B5CF6' },
  { key: 'info_received', label: 'Info Received', color: '#F59E0B' },
  { key: 'agreement_sent', label: 'Agreement Sent', color: '#EC4899' },
  { key: 'agreement_executed', label: 'Agreement Executed', color: '#10B981' },
  { key: 'employer_code_requested', label: 'Code Requested', color: '#14B8A6' },
  { key: 'pricebook_enabled', label: 'Pricebook Enabled', color: '#06B6D4' },
  { key: 'active', label: 'Active', color: '#22C55E' },
  { key: 'not_interested', label: 'Not Interested', color: '#EF4444' },
];

const STAGE_MAP = Object.fromEntries(STAGES.map(s => [s.key, s]));

const DOCUSIGN_URL = 'https://apps.docusign.com/api/maestro/v1/accounts/2096b5a0-309c-4f1f-9322-7589c918add2/workflow_definitions/f6411024-f6ce-4ff6-9b4b-bad178de5d52/trigger?hash=ZDk0ZDU5MGJiMzYwNDA4MzY5NGY2NTg2YzBjNTUxN2QyMmE1MTVjZWUxYjFiYmQwOTYzYTk5MDI4YzFiZDZkZGRkMGQyYTc1YmU1NTFmYzBhOGRjNzgwZjI2NTc2ZWUyMTVlNjdkNzJhZWE1MTUyY2ZmMDI4M2ZkZjkyNGYzZTdmMzk2NmEyNzZkMGE0ZDg2ZWUyYzc0Y2RkMGMyMmY4ZTIyMDk5ZjVhNjRjMzJiZWNmYjExZDgwMTY4ZTI4NTViNjUwMzEzOWM4NDZhMzVlMTk2ZGJmNGFkZWJmZmY1ZWY3NzRlOTNjNmZiNWRmZTEyMzg5OTVhYWI1OTljMzY0ZQ==';
const HELPDESK_CODE_URL = 'https://helpdesk.thejoint.com/support/catalog/items/145';
const HELPDESK_FEEDBACK_URL = 'https://helpdesk.thejoint.com/support/catalog/items/146';

/* ─── Styles ─────────────────────────────────────────────────────── */
const theme = {
  primary: '#00B4B9',
  primaryDark: '#009A9E',
  dark: '#1F2937',
  gray: '#6B7280',
  lightGray: '#F3F4F6',
  border: '#E5E7EB',
  white: '#FFFFFF',
  danger: '#EF4444',
  warning: '#F59E0B',
  success: '#22C55E',
};

const baseStyles = {
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
};

const css = {
  app: { ...baseStyles, display: 'flex', height: '100vh', background: theme.lightGray, margin: 0 },
  sidebar: {
    width: 240, background: theme.dark, color: theme.white, display: 'flex', flexDirection: 'column',
    flexShrink: 0, overflow: 'hidden',
  },
  sidebarLogo: {
    padding: '24px 20px', borderBottom: '1px solid rgba(255,255,255,0.1)',
    fontSize: 18, fontWeight: 700, letterSpacing: '-0.5px',
  },
  sidebarLogoSub: { fontSize: 11, color: theme.primary, fontWeight: 500, marginTop: 2 },
  navItem: (active) => ({
    padding: '10px 20px', cursor: 'pointer', fontSize: 14, fontWeight: 500,
    background: active ? 'rgba(0,180,185,0.15)' : 'transparent',
    color: active ? theme.primary : 'rgba(255,255,255,0.7)',
    borderLeft: active ? `3px solid ${theme.primary}` : '3px solid transparent',
    transition: 'all 0.15s',
  }),
  main: { flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' },
  header: {
    background: theme.white, padding: '16px 32px', borderBottom: `1px solid ${theme.border}`,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
  },
  headerTitle: { fontSize: 20, fontWeight: 700, color: theme.dark },
  content: { padding: 32, flex: 1 },
  card: {
    background: theme.white, borderRadius: 8, border: `1px solid ${theme.border}`,
    padding: 24, marginBottom: 16,
  },
  btn: (variant = 'primary') => ({
    padding: '8px 16px', borderRadius: 6, border: 'none', cursor: 'pointer',
    fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6,
    background: variant === 'primary' ? theme.primary : variant === 'danger' ? theme.danger : theme.white,
    color: variant === 'outline' ? theme.dark : theme.white,
    border: variant === 'outline' ? `1px solid ${theme.border}` : 'none',
    transition: 'opacity 0.15s',
  }),
  input: {
    width: '100%', padding: '8px 12px', borderRadius: 6, border: `1px solid ${theme.border}`,
    fontSize: 14, outline: 'none', boxSizing: 'border-box',
  },
  select: {
    padding: '8px 12px', borderRadius: 6, border: `1px solid ${theme.border}`,
    fontSize: 14, outline: 'none', background: theme.white,
  },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: theme.gray, marginBottom: 4 },
  badge: (color) => ({
    display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 11,
    fontWeight: 600, background: color + '20', color: color,
  }),
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
  th: {
    textAlign: 'left', padding: '10px 12px', borderBottom: `2px solid ${theme.border}`,
    fontSize: 12, fontWeight: 600, color: theme.gray, textTransform: 'uppercase', letterSpacing: '0.5px',
  },
  td: { padding: '12px', borderBottom: `1px solid ${theme.border}`, color: theme.dark },
  modal: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modalContent: {
    background: theme.white, borderRadius: 12, padding: 32, width: '90%', maxWidth: 600,
    maxHeight: '85vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  statCard: (color) => ({
    background: theme.white, borderRadius: 8, border: `1px solid ${theme.border}`,
    padding: '20px 24px', borderTop: `3px solid ${color}`, flex: 1, minWidth: 160,
  }),
};

/* ─── Refresh Button (with hover effect) ────────────────────────── */
function RefreshBtn({ onClick, label = 'Refresh' }) {
  const [hovered, setHovered] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const handleClick = async () => {
    setSpinning(true);
    try { await onClick(); } finally { setTimeout(() => setSpinning(false), 600); }
  };
  return (
    <button
      style={{
        ...css.btn('outline'),
        background: hovered ? theme.primary : theme.white,
        color: hovered ? theme.white : theme.primary,
        border: `1px solid ${theme.primary}`,
        transition: 'all 0.2s',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleClick}
    >
      <span style={{ display: 'inline-block', transition: 'transform 0.6s', transform: spinning ? 'rotate(360deg)' : 'none' }}>↻</span>
      {' '}{label}
    </button>
  );
}

/* ─── Helpers ────────────────────────────────────────────────────── */
function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function formatDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
function timeAgo(d) {
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/* ═══════════════════════════════════════════════════════════════════
   APP (wraps auth around the main CRM)
   ═══════════════════════════════════════════════════════════════════ */
export default function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Pick a sensible active company. Global admins start in the global view
  // ('all') on a fresh login; on resume their still-valid selection (a company
  // or the global view) is kept. Regular users keep a valid saved company,
  // otherwise default to their first company.
  const initActiveCompany = (u, fresh = false) => {
    const companies = u?.companies || [];
    if (u?.isGlobalAdmin) {
      if (fresh) { api.setActiveCompany('all'); return; }
      const saved = api.getActiveCompany();
      const ok = saved === 'all' || companies.some(c => c.id === saved);
      if (!ok) api.setActiveCompany('all');
      return;
    }
    const saved = api.getActiveCompany();
    const validSaved = saved && companies.some(c => c.id === saved);
    if (!validSaved) {
      api.setActiveCompany(companies[0]?.id || '');
    }
  };

  // Check for existing session on mount
  useEffect(() => {
    const token = api.getToken();
    if (token) {
      api.getMe()
        .then(u => { initActiveCompany(u); setUser(u); })
        .catch(() => api.clearToken())
        .finally(() => setAuthLoading(false));
    } else {
      setAuthLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const { token, user: u } = await api.login(email, password);
    api.setToken(token);
    initActiveCompany(u, true);   // fresh login → global admins start in the global view
    setUser(u);
  };

  // Self-serve signup: creates the company + admin (30-day free trial) and
  // boots straight into the new company.
  const signup = async (data) => {
    const { token, user: u, companyId } = await api.signup(data);
    api.setToken(token);
    if (companyId) api.setActiveCompany(companyId);
    // Leave /signup and land in the app at the root.
    window.history.replaceState({}, '', '/');
    setUser(u);
  };

  const logout = () => {
    api.clearToken();
    api.clearActiveCompany();
    setUser(null);
  };

  // Switch the active company and reload so every scoped view refetches.
  const switchCompany = (companyId) => {
    api.setActiveCompany(companyId);
    window.location.reload();
  };

  const activeCompanyId = api.getActiveCompany();
  const companies = user?.companies || [];
  const activeCompany = companies.find(c => c.id === activeCompanyId) || null;

  // Role is per-company: a user can be an admin in one company and a regular
  // user in another. Global admins act as ADMIN everywhere.
  const effectiveRole = (user?.isGlobalAdmin || activeCompanyId === 'all')
    ? 'ADMIN'
    : (activeCompany?.role || user?.role);
  const isAdmin = effectiveRole === 'ADMIN';
  const isManager = effectiveRole === 'ADMIN' || effectiveRole === 'MANAGER';
  // Franchisee is per-company: the current user's membership flag in the active company.
  const isFranchisee = activeCompanyId !== 'all' && !!activeCompany?.isFranchisee;

  if (authLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: theme.lightGray }}>
        <div style={{ fontSize: 16, color: theme.gray }}>Loading...</div>
      </div>
    );
  }

  // Self-serve signup lives at the dedicated /signup path and is reachable
  // whether or not someone is signed in (external link).
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  if (path === '/signup') return <SignupPage onSignup={signup} />;

  if (!user) {
    const params = new URLSearchParams(window.location.search);
    // A password-reset link (/?resetToken=...) opens the reset page
    const resetToken = params.get('resetToken');
    if (resetToken) return <ResetPasswordPage token={resetToken} />;
    return <LoginPage onLogin={login} />;
  }

  return (
    <AuthContext.Provider value={{ user, isAdmin, isManager, isFranchisee, logout, companies, activeCompanyId, activeCompany, switchCompany }}>
      <MainApp />
    </AuthContext.Provider>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   MAIN APP (authenticated)
   ═══════════════════════════════════════════════════════════════════ */
/* ─── App Tour (guided first-login walkthrough) ─────────────────────── */
// Build the tour steps for the viewer's role. Admins get more pages; global
// admins get a different set entirely (their view is cross-company).
// Each step may carry a `nav` (page to switch to) and/or a `target` CSS selector
// to spotlight. If no target is given but `nav` is, the matching sidebar item
// ([data-tour="nav-<key>"]) is highlighted. Steps with neither are centered.
function getTourSteps({ isGlobalAdmin, isAdmin, isManager }) {
  const welcome = {
    icon: '👋', title: 'Welcome to PerkPlan CRM',
    body: 'Here’s a quick walkthrough of the main areas — it moves between pages and points things out as it goes. Skip anytime, or reopen it later from Quick Links.',
  };
  if (isGlobalAdmin) {
    return [
      welcome,
      { nav: 'companies', icon: '🏢', title: 'Companies', body: 'Your home base — every company, searchable by name. Click into one to review its data and plan (read-only for you).' },
      { nav: 'settings', icon: '⚙', title: 'Settings (Defaults)', body: 'Global defaults — most importantly the shared SMTP server every company sends email through.' },
      { nav: 'email_templates', icon: '✉', title: 'Email Templates (Defaults)', body: 'The default templates new companies inherit. Edits flow to companies that haven’t customized their own.' },
      { nav: 'broadcast', icon: '📢', title: 'Broadcast Email', body: 'Send an announcement to selected recipients across companies.' },
      { target: '[data-tour="return-global"]', icon: '↩', title: 'Return to Global View', body: 'After drilling into a company, this button brings you back to the global view.' },
    ];
  }
  const steps = [
    welcome,
    { nav: 'dashboard', icon: '◉', title: 'Dashboard', body: 'Your overview: pipeline totals, recent activity, and how your leads are progressing.' },
    // Overview of the page first, then the button, then open the form itself.
    { nav: 'leads', icon: '☰', title: 'All Leads', body: 'Your leads list. Search, filter by stage, and open any row to see its details and history.' },
    { nav: 'leads', target: '[data-tour="new-lead"]', icon: '➕', title: 'Add a Lead', body: 'Use “+ New Lead” to add a prospect — let’s open the form.' },
    { showcase: true, action: 'newlead', icon: '📝', title: 'The New Lead Form', body: 'Enter the contact’s name, company, and email; choose the franchise and primary clinic; set the starting stage; and add any notes. A franchise is required to save.' },
    { showcase: true, action: 'sample', icon: '🔎', title: 'Working a Lead', body: 'Opening a lead shows this detail page. From here you advance it through the pipeline stages, edit its details, send the stage’s email, log calls and notes in the activity timeline, and attach documents or a company logo. (This is a sample lead.)' },
    { nav: 'pipeline', icon: '⬡', title: 'Pipeline', body: 'A stage-by-stage board of your leads — from first outreach through an executed agreement.' },
  ];
  if (isManager) {
    steps.push({ nav: 'users', icon: '👤', title: 'Users', body: 'Your team. Managers and admins can add or deactivate users in this company here.' });
  }
  if (isAdmin) {
    steps.push(
      { nav: 'settings', icon: '⚙', title: 'Franchises and Clinics', body: 'Manage your franchises and their clinic locations. Leads can be tied to a franchise and a primary clinic.' },
      { nav: 'settings', target: '[data-tour="add-franchise"]', icon: '➕', title: 'Add a Franchise', body: 'Use “+ Add Franchise” to create a franchise entity, then add its clinic locations underneath.' },
      { nav: 'my_plan', icon: '💳', title: 'My Plan', body: 'Your billing home: subscription, seats, and the required billing point of contact — set the billing contact here.' },
      { nav: 'email_templates', icon: '✉', title: 'Email Templates', body: 'Customize the emails your company sends at each pipeline stage.' },
      { nav: 'broadcast', icon: '📢', title: 'Broadcast Email', body: 'Send an announcement to a group of your contacts in one go.' },
    );
  }
  return steps;
}

function AppTour({ role, onStep, onClose }) {
  const steps = getTourSteps(role);
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const step = steps[i];
  const last = i === steps.length - 1;

  // On each step: let the app set up its UI (navigate, open the new-lead form,
  // show a sample lead), then locate + measure the element to spotlight,
  // retrying briefly while the page renders. Showcase steps have no spotlight.
  useEffect(() => {
    let cancelled = false;
    if (onStep) onStep(step);
    const selector = step.showcase ? null : (step.target || (step.nav ? `[data-tour="nav-${step.nav}"]` : null));
    if (!selector) { setRect(null); return; }
    const grab = () => {
      const el = document.querySelector(selector);
      if (el) { const r = el.getBoundingClientRect(); return { top: r.top, left: r.left, width: r.width, height: r.height }; }
      return null;
    };
    let tries = 0;
    const measure = () => {
      if (cancelled) return;
      const r = grab();
      if (r) setRect(r);
      else if (tries++ < 15) setTimeout(measure, 40);
      else setRect(null);
    };
    setRect(null);
    measure();
    const onWin = () => { const r = grab(); if (r) setRect(r); };
    window.addEventListener('resize', onWin);
    window.addEventListener('scroll', onWin, true);
    return () => { cancelled = true; window.removeEventListener('resize', onWin); window.removeEventListener('scroll', onWin, true); };
    // eslint-disable-next-line
  }, [i]);

  // Tooltip placement: beside a spotlighted element; pinned to a corner for
  // showcase steps (so the opened form / lead detail stays visible); else centered.
  const TW = 360;
  let tip = { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' };
  if (rect) {
    if (window.innerWidth - (rect.left + rect.width) > TW + 40) {
      tip = { left: rect.left + rect.width + 16, top: Math.max(16, Math.min(rect.top, window.innerHeight - 260)) };
    } else {
      tip = { left: Math.min(Math.max(16, rect.left), window.innerWidth - TW - 16), top: rect.top + rect.height + 14 };
    }
  } else if (step.showcase) {
    tip = { left: 24, bottom: 24 };
  }

  // Dim the screen only for centered non-showcase steps (welcome). Showcase and
  // spotlight steps stay transparent so the highlighted UI is fully visible.
  const dim = !rect && !step.showcase;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000 }}>
      {/* Blocks interaction with the app; dims only for centered intro steps. */}
      <div style={{ position: 'absolute', inset: 0, background: dim ? 'rgba(15,23,42,0.55)' : 'transparent' }} />
      {/* Spotlight: transparent hole with a huge shadow dims everything else. */}
      {rect && (
        <div style={{
          position: 'absolute', top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12,
          borderRadius: 8, boxShadow: '0 0 0 9999px rgba(15,23,42,0.55)', border: `2px solid ${theme.primary}`,
          pointerEvents: 'none', transition: 'top 0.2s, left 0.2s, width 0.2s, height 0.2s',
        }} />
      )}
      <div style={{
        position: 'absolute', width: TW, boxSizing: 'border-box', background: theme.white, borderRadius: 12, padding: 20,
        boxShadow: '0 12px 40px rgba(0,0,0,0.35)', ...tip,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ fontSize: 12, color: theme.gray, fontWeight: 600 }}>Step {i + 1} of {steps.length}</div>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', color: theme.gray, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>Skip ✕</button>
        </div>
        <div style={{ fontSize: 16, fontWeight: 800, color: theme.dark, marginBottom: 6 }}>{step.icon} {step.title}</div>
        <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: theme.gray }}>{step.body}</p>
        <div style={{ display: 'flex', gap: 5, margin: '16px 0 14px' }}>
          {steps.map((_, k) => (
            <div key={k} style={{ height: 5, flex: 1, borderRadius: 3, background: k <= i ? theme.primary : '#E5E7EB' }} />
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <button onClick={() => setI(x => Math.max(0, x - 1))} disabled={i === 0}
            style={{ ...css.btn('outline'), opacity: i === 0 ? 0.5 : 1 }}>Back</button>
          {last
            ? <button onClick={onClose} style={css.btn('primary')}>Finish</button>
            : <button onClick={() => setI(x => Math.min(steps.length - 1, x + 1))} style={css.btn('primary')}>Next</button>}
        </div>
      </div>
    </div>
  );
}

function MainApp() {
  const { user, isAdmin, isManager, logout, companies, activeCompanyId, activeCompany, switchCompany } = useAuth();
  const [page, setPage] = useState('dashboard');
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(null); // null=closed, or template key string
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [dashboard, setDashboard] = useState(null);
  const [settings, setSettings] = useState({});
  const [franchisees, setFranchisees] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [toast, setToast] = useState(null);
  const [renewalDismissed, setRenewalDismissed] = useState(false);
  // App tour: auto-opens only on a user's first-ever login (a transient
  // `firstLogin` flag the login/signup response derives from lastLoginAt — no
  // stored flag). Reopenable anytime from Quick Links. Closing just dismisses;
  // it won't auto-reappear because later logins aren't "first".
  const [showTour, setShowTour] = useState(!!user.firstLogin);
  const tourRole = { isGlobalAdmin: !!user.isGlobalAdmin, isAdmin, isManager };
  // A fabricated lead used only to showcase the lead-detail page during the tour.
  const tourSampleLead = {
    id: 'tour-sample', companyName: 'Acme Wellness (sample)', contactName: 'Jane Doe',
    email: 'jane.doe@example.com', phone: '(555) 123-4567', stage: 'initial_outreach',
    source: 'Referral', notes: 'This is a sample lead shown during the tour — your real leads appear here.',
    createdBy: user.id, assignedTo: null, franchisee: null, primaryClinic: null,
    activities: [
      { id: 'ts1', type: 'note', description: 'Left a voicemail introducing the Perk Program.', createdAt: new Date().toISOString() },
      { id: 'ts2', type: 'stage_change', description: 'Stage changed from "new_lead" to "initial_outreach".', createdAt: new Date().toISOString() },
    ],
  };
  // Drive the app's UI for each tour step: plain nav, open the new-lead form, or
  // show the sample lead detail. Clears transient UI between steps.
  const tourStep = (step) => {
    setShowLeadModal(false);
    if (step.action === 'newlead') { setSelectedLead(null); setPage('leads'); setShowLeadModal(true); }
    else if (step.action === 'sample') { setSelectedLead(tourSampleLead); setPage('lead_detail'); }
    else { setSelectedLead(null); if (step.nav) setPage(step.nav); }
  };
  const closeTour = useCallback(() => {
    setShowTour(false); setShowLeadModal(false); setSelectedLead(null); setPage('dashboard');
  }, []);

  // The active company is gated when its subscription is inactive OR it has no
  // billing point of contact set. Admins are pinned to the pages needed to
  // resolve it (My Plan / Settings); everyone else gets a "contact your admin"
  // notice. The global "all" view is never gated.
  const specificCompany = activeCompanyId !== 'all' && activeCompany;
  const subInactive = !!(specificCompany && !activeCompany.accessActive);
  const pocMissing = !!(specificCompany && !activeCompany.pointOfContactSet);
  const restricted = subInactive || pocMissing;
  const showContactAdminModal = restricted && !isAdmin && !renewalDismissed;

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), type === 'warning' ? 6000 : 3500);
  }, []);

  const loadLeads = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (stageFilter) params.stage = stageFilter;
      setLeads(await api.getLeads(params));
    } catch (e) { showToast(e.message, 'error'); }
  }, [search, stageFilter, showToast]);

  const loadDashboard = useCallback(async () => {
    try { setDashboard(await api.getDashboard()); } catch (e) { showToast(e.message, 'error'); }
  }, [showToast]);

  const loadSettings = useCallback(async () => {
    try { setSettings(await api.getSettings()); } catch (e) { /* silent */ }
  }, []);

  const loadFranchisees = useCallback(async () => {
    try { setFranchisees(await api.getFranchisees()); } catch (e) { /* silent */ }
  }, []);

  const loadAssignableUsers = useCallback(async () => {
    try { setAssignableUsers(await api.getAssignableUsers()); } catch (e) { /* silent */ }
  }, []);

  // Skip data loads when locked to the billing page (those endpoints are gated)
  // or in the global "all" reporting view (no per-company leads/pipeline there).
  const activeCompanyIdForLoads = activeCompanyId;   // 'all' → global view
  useEffect(() => {
    if (!restricted && activeCompanyIdForLoads !== 'all') loadLeads();
  }, [restricted, activeCompanyIdForLoads, loadLeads]);
  useEffect(() => {
    // Settings is ungated (billing point of contact is needed even while the
    // subscription is inactive), so load it regardless; global view loads defaults.
    loadSettings();
    if (!restricted && activeCompanyIdForLoads !== 'all') { loadDashboard(); loadFranchisees(); loadAssignableUsers(); }
  }, [restricted, activeCompanyIdForLoads, loadDashboard, loadSettings, loadFranchisees, loadAssignableUsers]);


  const openLead = async (id) => {
    try {
      const lead = await api.getLead(id);
      setSelectedLead(lead);
      setPage('lead_detail');
    } catch (e) { showToast(e.message, 'error'); }
  };

  // Global admin "All Companies" reporting view: cross-company admin/config
  // only — no leads, pipeline, dashboard or per-company billing.
  const globalView = activeCompanyId === 'all';

  let NAV;
  if (restricted) {
    // No active subscription / no billing POC → admins can reach only the
    // billing page (My Plan, which also holds the billing point of contact) and
    // Users (to deactivate users for a cheaper plan). Franchises and Clinics is
    // gated behind an active subscription with a POC set.
    NAV = isAdmin
      ? [
          { key: 'my_plan', label: 'My Plan', icon: '💳' },
          { key: 'users', label: 'Users', icon: '👤' },
        ]
      : [];
  } else if (globalView) {
    NAV = [
      { key: 'companies', label: 'Companies', icon: '🏢' },
      { key: 'settings', label: 'Settings (Defaults)', icon: '⚙' },
      { key: 'email_templates', label: 'Email Templates (Defaults)', icon: '✉' },
      { key: 'broadcast', label: 'Broadcast Email', icon: '📢' },
      { key: 'change_password', label: 'Password', icon: '🔑' },
    ];
  } else {
    NAV = [
      { key: 'dashboard', label: 'Dashboard', icon: '◉' },
      { key: 'leads', label: 'All Leads', icon: '☰' },
      { key: 'pipeline', label: 'Pipeline', icon: '⬡' },
    ];
    // Admins and Managers get the Users nav item
    if (isManager) {
      NAV.push({ key: 'users', label: 'Users', icon: '👤' });
    }
    // Admins get Settings, the billing plan (below Settings), Email Templates, etc.
    if (isAdmin) {
      NAV.push({ key: 'settings', label: 'Franchises and Clinics', icon: '⚙' });
      NAV.push({ key: 'my_plan', label: 'My Plan', icon: '💳' });
      NAV.push({ key: 'email_templates', label: 'Email Templates', icon: '✉' });
      NAV.push({ key: 'broadcast', label: 'Broadcast Email', icon: '📢' });
    }
    // Everyone gets change password
    NAV.push({ key: 'change_password', label: 'Password', icon: '🔑' });
  }

  // While restricted, an admin can be on the plan page or the Users page
  // (to deactivate users); anything else falls back to the plan page. In the
  // global view, fall back to the companies list.
  let effectivePage;
  if (restricted && isAdmin) {
    // Only My Plan (billing + POC) and Users are reachable while restricted;
    // anything else (incl. Franchises and Clinics) falls back to My Plan.
    effectivePage = ['my_plan', 'users'].includes(page)
      ? page
      : 'my_plan';
  } else {
    effectivePage = page;
  }
  if (globalView && !NAV.some(n => n.key === effectivePage)) effectivePage = 'companies';

  return (
    <div style={css.app}>
      {/* Sidebar */}
      <div style={css.sidebar}>
        <div style={css.sidebarLogo}>
          PerkPlan <span style={{ color: theme.primary }}>CRM</span>
          <div style={css.sidebarLogoSub}>The Joint Chiropractic</div>
        </div>
        <div style={{ padding: '12px 0', flex: 1 }}>
          {NAV.map(n => (
            <div key={n.key} data-tour={`nav-${n.key}`} style={css.navItem(effectivePage === n.key || (effectivePage === 'lead_detail' && n.key === 'leads'))}
              onClick={() => { setPage(n.key); setSelectedLead(null); }}>
              <span style={{ marginRight: 10 }}>{n.icon}</span> {n.label}
            </div>
          ))}
        </div>
        <div style={{ padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.1)', fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
          <div style={{ marginBottom: 8 }}>
            Signed in as <span style={{ color: theme.primary, fontWeight: 600 }}>{user.firstName} {user.lastName}</span>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 2 }}>{user.email}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 6 }}>
              {activeCompanyId === 'all' ? 'View: ' : 'Company: '}
              <span style={{ color: theme.primary, fontWeight: 600 }}>
                {activeCompanyId === 'all' ? 'Global View' : (activeCompany?.name || '—')}
              </span>
            </div>
          </div>

          {/* Company switcher */}
          {user.isGlobalAdmin ? (
            // Global admins switch into companies from the Companies page; here
            // they only get a button back to the global view (inactive there).
            <div style={{ marginBottom: 10 }}>
              <button
                data-tour="return-global"
                onClick={() => { if (activeCompanyId !== 'all') switchCompany('all'); }}
                disabled={activeCompanyId === 'all'}
                style={{
                  width: '100%', padding: '7px 8px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                  background: activeCompanyId === 'all' ? 'rgba(255,255,255,0.04)' : theme.primary,
                  color: activeCompanyId === 'all' ? 'rgba(255,255,255,0.4)' : theme.white,
                  border: activeCompanyId === 'all' ? '1px solid rgba(255,255,255,0.1)' : 'none',
                  cursor: activeCompanyId === 'all' ? 'default' : 'pointer',
                }}
              >↩ Return to Global View</button>
            </div>
          ) : companies.length > 1 ? (
            <div style={{ marginBottom: 10 }}>
              <select
                value={activeCompanyId || ''}
                onChange={(e) => switchCompany(e.target.value)}
                style={{
                  width: '100%', padding: '6px 8px', borderRadius: 6, fontSize: 12,
                  background: 'rgba(255,255,255,0.08)', color: theme.white,
                  border: '1px solid rgba(255,255,255,0.2)', outline: 'none', cursor: 'pointer',
                }}
              >
                {companies.map(c => (
                  <option key={c.id} value={c.id} style={{ color: '#1F2937' }}>{c.name}</option>
                ))}
              </select>
            </div>
          ) : null}

          <div style={{ cursor: 'pointer', color: '#EF4444', fontWeight: 600, marginBottom: 10 }} onClick={logout}>
            Sign Out
          </div>
          <div data-tour="quick-links" style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 8 }}>
            Quick Links:
            <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <a href={DOCUSIGN_URL} target="_blank" rel="noreferrer" style={{ color: theme.primary, fontSize: 11, textDecoration: 'none' }}>DocuSign Webform</a>
              <a href={HELPDESK_CODE_URL} target="_blank" rel="noreferrer" style={{ color: theme.primary, fontSize: 11, textDecoration: 'none' }}>Request Employer Code</a>
              <a href={HELPDESK_FEEDBACK_URL} target="_blank" rel="noreferrer" style={{ color: theme.primary, fontSize: 11, textDecoration: 'none' }}>Submit Feedback</a>
              <span onClick={() => setShowTour(true)} style={{ color: theme.primary, fontSize: 11, cursor: 'pointer' }}>Take the App Tour</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main */}
      <div style={css.main}>
        {/* Red banner when no billing point of contact is set */}
        {pocMissing && (
          <div style={{ background: theme.danger, color: theme.white, padding: '10px 32px', fontSize: 13, fontWeight: 600 }}>
            ⚠ {activeCompany?.name || 'This company'} has no billing point of contact set.{' '}
            {isAdmin ? 'Set one on the My Plan page to continue using the app.' : 'An admin must set one on the My Plan page to continue.'}
          </div>
        )}
        {restricted && !isAdmin ? (
          <>
            <div style={css.header}><div style={css.headerTitle}>{pocMissing && !subInactive ? 'Billing point of contact required' : 'Subscription inactive'}</div></div>
            <div style={css.content}>
              <div style={css.card}>
                {pocMissing && !subInactive ? (
                  <>
                    <div style={{ fontSize: 18, fontWeight: 700, color: theme.dark, marginBottom: 8 }}>A billing point of contact is required</div>
                    <div style={{ fontSize: 14, color: theme.gray }}>
                      {activeCompany?.name || 'This company'} can't be used until an administrator sets a billing point of contact on the My Plan page.
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 18, fontWeight: 700, color: theme.dark, marginBottom: 8 }}>This company's subscription is inactive</div>
                    <div style={{ fontSize: 14, color: theme.gray }}>
                      Access to {activeCompany?.name || 'this company'} is paused until the subscription is active. Please contact your administrator to resolve billing.
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        ) : restricted && isAdmin ? (
          effectivePage === 'users'
            ? <UsersPage showToast={showToast} onGoToPlan={() => setPage('my_plan')} />
            : effectivePage === 'settings'
              ? <SettingsPage settings={settings} restrictedMode onSave={async (data) => {
                  await api.updateSettings(data);
                  // Reload so the gate lifts once a billing POC is set.
                  window.location.reload();
                }} showToast={showToast} franchisees={franchisees} onFranchiseesChange={loadFranchisees} />
              : <PlanPage showToast={showToast} />
        ) : (
          <>
            {!globalView && effectivePage === 'dashboard' && <DashboardPage dashboard={dashboard} onOpenLead={openLead} onRefresh={loadDashboard} />}
            {!globalView && effectivePage === 'leads' && (
              <LeadsPage leads={leads} search={search} setSearch={setSearch} stageFilter={stageFilter}
                setStageFilter={setStageFilter} onOpenLead={openLead} onRefresh={loadLeads}
                onNewLead={() => { setSelectedLead(null); setShowLeadModal(true); }} />
            )}
            {!globalView && effectivePage === 'lead_detail' && selectedLead && (
              <LeadDetailPage lead={selectedLead} franchisees={franchisees} assignableUsers={assignableUsers} onBack={() => { setPage('leads'); loadLeads(); }}
                onRefresh={() => openLead(selectedLead.id)} showToast={showToast}
                onSendEmail={(tpl) => setShowEmailModal(tpl || 'initial_interest')} />
            )}
            {!globalView && effectivePage === 'pipeline' && <PipelinePage leads={leads} onOpenLead={openLead} onRefresh={loadLeads} showToast={showToast} />}
            {effectivePage === 'settings' && isAdmin && <SettingsPage settings={settings} globalDefaults={globalView} onSave={async (data) => {
              await api.updateSettings(data); await loadSettings(); showToast(globalView ? 'Global defaults saved' : 'Settings saved');
            }} showToast={showToast} franchisees={franchisees} onFranchiseesChange={loadFranchisees} />}
            {!globalView && effectivePage === 'users' && isManager && <UsersPage showToast={showToast} onGoToPlan={() => setPage('my_plan')} />}
            {globalView && effectivePage === 'companies' && <GlobalCompaniesPage showToast={showToast} />}
            {effectivePage === 'email_templates' && isAdmin && <EmailTemplatesPage showToast={showToast} />}
            {effectivePage === 'broadcast' && isAdmin && <BroadcastEmailPage showToast={showToast} />}
            {!globalView && effectivePage === 'my_plan' && isAdmin && <PlanPage showToast={showToast} settings={settings} reloadSettings={loadSettings} />}
            {effectivePage === 'change_password' && <ChangePasswordPage showToast={showToast} />}
          </>
        )}
      </div>

      {/* Lead Modal */}
      {showLeadModal && (
        <LeadModal lead={selectedLead} franchisees={franchisees} assignableUsers={assignableUsers} onClose={() => setShowLeadModal(false)}
          onSave={async (data) => {
            try {
              if (selectedLead?.id) await api.updateLead(selectedLead.id, data);
              else await api.createLead(data);
              setShowLeadModal(false); loadLeads(); loadDashboard();
              showToast(selectedLead ? 'Lead updated' : 'Lead created');
            } catch (e) { showToast(e.message, 'error'); }
          }} />
      )}

      {/* Email Modal */}
      {showEmailModal && selectedLead && (
        <EmailModal lead={selectedLead} initialTemplate={showEmailModal}
          onClose={() => setShowEmailModal(null)}
          showToast={showToast} onSent={() => { setShowEmailModal(null); openLead(selectedLead.id); }} />
      )}

      {/* Renewal-due popup (shown once per load when on/after the renewal date) */}
      {/* No-active-subscription notice for non-admins (admins are taken to My Plan) */}
      {showContactAdminModal && (
        <div style={css.modal} onClick={() => setRenewalDismissed(true)}>
          <div style={{ ...css.modalContent, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 700, color: theme.dark }}>
              Subscription inactive
            </h2>
            <p style={{ fontSize: 14, color: theme.gray, marginBottom: 20 }}>
              <strong>{activeCompany.name}</strong> doesn't have an active subscription, so access is paused.
              You don't have permission to manage billing — please contact your administrator to reactivate it.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button style={css.btn('primary')} onClick={() => setRenewalDismissed(true)}>OK</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, padding: '12px 20px', borderRadius: 8,
          background: toast.type === 'error' ? theme.danger : toast.type === 'warning' ? theme.warning : theme.success, color: theme.white,
          fontSize: 14, fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,0.2)', zIndex: 9999,
        }}>
          {toast.msg}
        </div>
      )}

      {/* App tour (first login + reopenable from Quick Links) */}
      {showTour && <AppTour role={tourRole} onStep={tourStep} onClose={closeTour} />}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   GLOBAL ADMIN — COMPANIES LIST
   ═══════════════════════════════════════════════════════════════════ */
function GlobalCompaniesPage({ showToast }) {
  const { switchCompany } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [hideInactive, setHideInactive] = useState(false);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.getCompaniesOverview(); setCompanies(res.companies || []); }
    catch (e) { showToast(e.message, 'error'); }
    setLoading(false);
  }, [showToast]);
  useEffect(() => { load(); }, [load]);

  const term = q.trim().toLowerCase();
  const shown = companies
    .filter(c => !hideInactive || c.accessActive)
    .filter(c => !term || (c.name || '').toLowerCase().includes(term));

  return (
    <>
      <div style={css.header}>
        <div style={css.headerTitle}>Companies</div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <label style={{ fontSize: 13, color: theme.gray, display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={hideInactive} onChange={e => setHideInactive(e.target.checked)} />
            Hide without active subscription
          </label>
          <RefreshBtn onClick={load} />
        </div>
      </div>
      <div style={css.content}>
        {/* Prominent company search */}
        <div style={{ position: 'relative', marginBottom: 20 }}>
          <span style={{
            position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
            fontSize: 18, color: theme.gray, pointerEvents: 'none',
          }}>🔍</span>
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search companies by name…"
            style={{
              ...css.input, width: '100%', padding: '14px 16px 14px 44px',
              fontSize: 16, borderRadius: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            }}
          />
          {q && (
            <span onClick={() => setQ('')} style={{
              position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
              fontSize: 18, color: theme.gray, cursor: 'pointer',
            }}>×</span>
          )}
        </div>

        <div style={css.card}>
          <table style={css.table}>
            <thead>
              <tr>
                <th style={css.th}>Company</th>
                <th style={css.th}>EIN</th>
                <th style={css.th}>Seats</th>
                <th style={css.th}>Billing Point of Contact</th>
                <th style={css.th}>Status</th>
                <th style={css.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ ...css.td, textAlign: 'center', color: theme.gray }}>Loading…</td></tr>
              ) : shown.length === 0 ? (
                <tr><td colSpan={6} style={{ ...css.td, textAlign: 'center', color: theme.gray }}>
                  No companies{hideInactive ? ' with an active subscription' : ''}.
                </td></tr>
              ) : shown.map(c => (
                <tr key={c.id}>
                  <td style={{ ...css.td, fontWeight: 600 }}>{c.name}</td>
                  <td style={{ ...css.td, fontSize: 13 }}>{c.ein || <span style={{ color: theme.gray }}>—</span>}</td>
                  <td style={css.td}>{c.userSlots}</td>
                  <td style={{ ...css.td, fontSize: 13 }}>{c.pointOfContactEmail || <span style={{ color: theme.gray }}>—</span>}</td>
                  <td style={css.td}>
                    <span style={css.badge(c.accessActive ? theme.success : theme.gray)}>
                      {c.accessActive ? 'Active' : 'No subscription'}
                    </span>
                  </td>
                  <td style={css.td}>
                    <button style={{ ...css.btn('primary'), padding: '4px 12px', fontSize: 12 }}
                      onClick={() => switchCompany(c.id)}>
                      Open ↗
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   DASHBOARD
   ═══════════════════════════════════════════════════════════════════ */
function DashboardPage({ dashboard, onOpenLead, onRefresh }) {
  const { companies = [], activeCompanyId, switchCompany, user } = useAuth();
  if (!dashboard) return <div style={css.content}><p>Loading dashboard...</p></div>;

  const { totalLeads, stageCounts, recentActivities, overdueFollowUps, myAssignedLeads = [], leaderboard = [] } = dashboard;

  /* ── Trophy SVG component ─────────────────────────────────────── */
  const Trophy = ({ place, name, activePlans, totalLeads: tl }) => {
    const configs = {
      1: { cup: '#FFD700', shine: '#FFF8DC', base: '#DAA520', baseAccent: '#B8860B', label: '1st Place', height: 140 },
      2: { cup: '#C0C0C0', shine: '#F0F0F0', base: '#A9A9A9', baseAccent: '#808080', label: '2nd Place', height: 120 },
      3: { cup: '#CD7F32', shine: '#FFDAB9', base: '#A0522D', baseAccent: '#8B4513', label: '3rd Place', height: 110 },
    };
    const c = configs[place];
    const stat = activePlans > 0 ? `${activePlans} active plan${activePlans !== 1 ? 's' : ''}` : `${tl} lead${tl !== 1 ? 's' : ''} submitted`;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: place === 1 ? 16 : 14, color: theme.dark, marginBottom: 4, textAlign: 'center', minHeight: 20 }}>
          {name || '—'}
        </div>
        <div style={{ fontSize: 11, color: theme.gray, marginBottom: 8, textAlign: 'center' }}>{name ? stat : ''}</div>
        <svg width={place === 1 ? 110 : 90} height={c.height} viewBox="0 0 110 140" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))' }}>
          {/* Handles */}
          <ellipse cx="15" cy="52" rx="14" ry="22" fill="none" stroke={c.cup} strokeWidth="5" />
          <ellipse cx="95" cy="52" rx="14" ry="22" fill="none" stroke={c.cup} strokeWidth="5" />
          {/* Cup body */}
          <path d={`M25,20 L25,70 Q25,95 55,95 Q85,95 85,70 L85,20 Z`} fill={c.cup} />
          {/* Cup rim */}
          <rect x="20" y="16" width="70" height="8" rx="3" fill={c.baseAccent} />
          {/* Shine highlight */}
          <path d="M38,28 L38,65 Q38,80 48,82 L42,82 Q32,80 32,65 L32,28 Z" fill={c.shine} opacity="0.45" />
          {/* Stem */}
          <rect x="48" y="95" width="14" height="14" rx="2" fill={c.baseAccent} />
          {/* Base */}
          <rect x="32" y="109" width="46" height="10" rx="4" fill={c.base} />
          <rect x="28" y="119" width="54" height="12" rx="4" fill={c.baseAccent} />
          {/* Label on base */}
          <text x="55" y="128" textAnchor="middle" fill={c.shine} fontSize="8" fontWeight="700" fontFamily="Inter, sans-serif">{c.label.toUpperCase()}</text>
          {/* Label on cup */}
          <text x="55" y="54" textAnchor="middle" fill={c.baseAccent} fontSize="11" fontWeight="800" fontFamily="Inter, sans-serif">{c.label}</text>
        </svg>
      </div>
    );
  };

  return (
    <>
      <div style={css.header}>
        <div style={css.headerTitle}>Dashboard</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {user?.isGlobalAdmin ? (
            <button
              style={{ ...css.btn('outline'), padding: '6px 12px', fontSize: 12 }}
              onClick={() => { if (activeCompanyId !== 'all') switchCompany('all'); }}
              disabled={activeCompanyId === 'all'}>
              ↩ Return to Global View
            </button>
          ) : companies.length > 1 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: theme.gray }}>Company:</span>
              <select
                value={activeCompanyId || ''}
                onChange={(e) => switchCompany(e.target.value)}
                style={css.select}
              >
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          ) : null}
          <RefreshBtn onClick={onRefresh} />
        </div>
      </div>
      <div style={css.content}>
        {/* Stats row */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
          <div style={css.statCard(theme.primary)}>
            <div style={{ fontSize: 28, fontWeight: 700, color: theme.dark }}>{totalLeads}</div>
            <div style={{ fontSize: 12, color: theme.gray, fontWeight: 600 }}>Total Leads</div>
          </div>
          <div style={css.statCard(theme.success)}>
            <div style={{ fontSize: 28, fontWeight: 700, color: theme.dark }}>{stageCounts.active || 0}</div>
            <div style={{ fontSize: 12, color: theme.gray, fontWeight: 600 }}>Active Partners</div>
          </div>
          <div style={css.statCard(theme.warning)}>
            <div style={{ fontSize: 28, fontWeight: 700, color: theme.dark }}>
              {(stageCounts.agreement_sent || 0) + (stageCounts.info_requested || 0)}
            </div>
            <div style={{ fontSize: 12, color: theme.gray, fontWeight: 600 }}>In Progress</div>
          </div>
          <div style={css.statCard(theme.danger)}>
            <div style={{ fontSize: 28, fontWeight: 700, color: theme.dark }}>{overdueFollowUps.length}</div>
            <div style={{ fontSize: 12, color: theme.gray, fontWeight: 600 }}>Overdue Follow-ups</div>
          </div>
        </div>

        {/* Leaderboard */}
        {leaderboard.length > 0 && (
          <div style={{ ...css.card, marginBottom: 24, background: 'linear-gradient(135deg, #FFFDF5 0%, #FFF 50%, #F5F9FF 100%)' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 700, color: theme.dark, textAlign: 'center' }}>
              Leaderboard
            </h3>
            <p style={{ textAlign: 'center', fontSize: 12, color: theme.gray, margin: '0 0 16px' }}>Top performers by active plans</p>
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 12, padding: '0 16px' }}>
              {/* 2nd place (left) */}
              <Trophy place={2}
                name={leaderboard[1] ? `${leaderboard[1].firstName} ${leaderboard[1].lastName}` : null}
                activePlans={leaderboard[1]?.activePlans || 0}
                totalLeads={leaderboard[1]?.totalLeads || 0} />
              {/* 1st place (center, tallest) */}
              <Trophy place={1}
                name={leaderboard[0] ? `${leaderboard[0].firstName} ${leaderboard[0].lastName}` : null}
                activePlans={leaderboard[0]?.activePlans || 0}
                totalLeads={leaderboard[0]?.totalLeads || 0} />
              {/* 3rd place (right) */}
              <Trophy place={3}
                name={leaderboard[2] ? `${leaderboard[2].firstName} ${leaderboard[2].lastName}` : null}
                activePlans={leaderboard[2]?.activePlans || 0}
                totalLeads={leaderboard[2]?.totalLeads || 0} />
            </div>
          </div>
        )}

        {/* My Assigned Leads */}
        {myAssignedLeads.length > 0 && (
          <div style={{ ...css.card, marginBottom: 24 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: theme.dark }}>
              My Assigned Leads <span style={{ fontSize: 12, fontWeight: 500, color: theme.gray }}>({myAssignedLeads.length})</span>
            </h3>
            <table style={css.table}>
              <thead>
                <tr>
                  <th style={css.th}>Company</th>
                  <th style={css.th}>Contact</th>
                  <th style={css.th}>Stage</th>
                  <th style={css.th}>Franchisee</th>
                  <th style={css.th}>Created By</th>
                  <th style={css.th}>Updated</th>
                </tr>
              </thead>
              <tbody>
                {myAssignedLeads.map(l => {
                  const stage = STAGE_MAP[l.stage] || { label: l.stage, color: '#999' };
                  return (
                    <tr key={l.id} onClick={() => onOpenLead(l.id)} style={{ cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = theme.lightGray}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ ...css.td, fontWeight: 600 }}>{l.companyName}</td>
                      <td style={css.td}>{l.contactName}</td>
                      <td style={css.td}><span style={css.badge(stage.color)}>{stage.label}</span></td>
                      <td style={{ ...css.td, fontSize: 12 }}>{l.franchisee ? l.franchisee.name : '—'}</td>
                      <td style={{ ...css.td, fontSize: 12 }}>{l.owner ? `${l.owner.firstName} ${l.owner.lastName}` : '—'}</td>
                      <td style={{ ...css.td, fontSize: 12, color: theme.gray }}>{timeAgo(l.updatedAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pipeline overview */}
        <div style={{ ...css.card, marginTop: 8 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: theme.dark }}>Pipeline Overview</h3>
          <div style={{ display: 'flex', gap: 4 }}>
            {STAGES.map(s => {
              const count = stageCounts[s.key] || 0;
              return (
                <div key={s.key} style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ height: 6, background: count > 0 ? s.color : theme.border, borderRadius: 3, marginBottom: 6 }} />
                  <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{count}</div>
                  <div style={{ fontSize: 10, color: theme.gray, lineHeight: 1.3 }}>{s.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginTop: 8 }}>
          {/* Overdue follow-ups */}
          <div style={css.card}>
            <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: theme.dark }}>Overdue Follow-ups</h3>
            {overdueFollowUps.length === 0 ? (
              <p style={{ color: theme.gray, fontSize: 13 }}>No overdue follow-ups. You're all caught up!</p>
            ) : (
              overdueFollowUps.map(l => (
                <div key={l.id} onClick={() => onOpenLead(l.id)}
                  style={{ padding: '8px 0', borderBottom: `1px solid ${theme.border}`, cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{l.companyName}</div>
                    <div style={{ fontSize: 12, color: theme.gray }}>{l.contactName}</div>
                  </div>
                  <div style={{ fontSize: 12, color: theme.danger, fontWeight: 600 }}>{formatDate(l.followUpDate)}</div>
                </div>
              ))
            )}
          </div>

          {/* Recent activity */}
          <div style={css.card}>
            <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: theme.dark }}>Recent Activity</h3>
            {recentActivities.length === 0 ? (
              <p style={{ color: theme.gray, fontSize: 13 }}>No recent activity yet.</p>
            ) : (
              recentActivities.map(a => (
                <div key={a.id} style={{ padding: '6px 0', borderBottom: `1px solid ${theme.border}`, fontSize: 13 }}>
                  <span style={{ fontWeight: 600 }}>{a.Lead?.companyName || 'Unknown'}</span>
                  <span style={{ color: theme.gray }}> — {a.description}</span>
                  <div style={{ fontSize: 11, color: theme.gray }}>{timeAgo(a.createdAt)}</div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   LEADS LIST
   ═══════════════════════════════════════════════════════════════════ */
function LeadsPage({ leads, search, setSearch, stageFilter, setStageFilter, onOpenLead, onRefresh, onNewLead }) {
  return (
    <>
      <div style={css.header}>
        <div style={css.headerTitle}>Leads</div>
        <button data-tour="new-lead" style={css.btn('primary')} onClick={onNewLead}>+ New Lead</button>
      </div>
      <div style={css.content}>
        {/* Filters */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
          <input style={{ ...css.input, maxWidth: 300 }} placeholder="Search leads..." value={search}
            onChange={e => setSearch(e.target.value)} />
          <select style={css.select} value={stageFilter} onChange={e => setStageFilter(e.target.value)}>
            <option value="">All Stages</option>
            {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <RefreshBtn onClick={onRefresh} />
        </div>

        {/* Table */}
        <div style={css.card}>
          <table style={css.table}>
            <thead>
              <tr>
                <th style={css.th}>Company</th>
                <th style={css.th}>Contact</th>
                <th style={css.th}>Email</th>
                <th style={css.th}>Stage</th>
                <th style={css.th}>Franchisee</th>
                <th style={css.th}>Owner</th>
                <th style={css.th}>Assigned To</th>
                <th style={css.th}>Follow-up</th>
                <th style={css.th}>Updated</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 ? (
                <tr><td colSpan={9} style={{ ...css.td, textAlign: 'center', color: theme.gray }}>No leads found. Click "+ New Lead" to add one.</td></tr>
              ) : leads.map(l => {
                const stage = STAGE_MAP[l.stage] || { label: l.stage, color: '#999' };
                const isOverdue = l.followUpDate && new Date(l.followUpDate) <= new Date();
                const ownerName = l.owner ? `${l.owner.firstName} ${l.owner.lastName}` : '—';
                const assigneeName = l.assignee ? `${l.assignee.firstName} ${l.assignee.lastName}` : '—';
                const franName = l.franchisee ? l.franchisee.name : '—';
                return (
                  <tr key={l.id} onClick={() => onOpenLead(l.id)} style={{ cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = theme.lightGray}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ ...css.td, fontWeight: 600 }}>{l.companyName}</td>
                    <td style={css.td}>{l.contactName}</td>
                    <td style={{ ...css.td, fontSize: 13 }}>{l.email || '—'}</td>
                    <td style={css.td}><span style={css.badge(stage.color)}>{stage.label}</span></td>
                    <td style={{ ...css.td, fontSize: 12 }}>{franName}</td>
                    <td style={{ ...css.td, fontSize: 12 }}>{ownerName}</td>
                    <td style={{ ...css.td, fontSize: 12 }}>{assigneeName}</td>
                    <td style={{ ...css.td, color: isOverdue ? theme.danger : theme.gray, fontWeight: isOverdue ? 600 : 400, fontSize: 13 }}>
                      {formatDate(l.followUpDate)}
                    </td>
                    <td style={{ ...css.td, fontSize: 12, color: theme.gray }}>{timeAgo(l.updatedAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   LEAD DETAIL
   ═══════════════════════════════════════════════════════════════════ */
function LeadDetailPage({ lead, franchisees = [], assignableUsers = [], onBack, onRefresh, showToast, onSendEmail }) {
  const { user, isManager, isFranchisee } = useAuth();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(lead);
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState('note');
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [franchiseeBlock, setFranchiseeBlock] = useState(false);   // DocuSign step gate popup
  const stage = STAGE_MAP[lead.stage] || { label: lead.stage, color: '#999' };
  const canEdit = isManager || lead.createdBy === user.id || lead.assignedTo === user.id;

  const toggleSection = (key) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  const sectionHeader = (key, title, rightContent) => (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: collapsed[key] ? 0 : 16,
      cursor: 'pointer', userSelect: 'none',
    }} onClick={() => toggleSection(key)}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          display: 'inline-block', fontSize: 10, color: theme.gray,
          transform: collapsed[key] ? 'rotate(-90deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s',
        }}>▼</span>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: theme.dark }}>{title}</h3>
      </div>
      {rightContent && <div onClick={e => e.stopPropagation()}>{rightContent}</div>}
    </div>
  );

  const [logoInfo, setLogoInfo] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoTimestamp, setLogoTimestamp] = useState(Date.now());

  // Load documents for this lead
  const loadDocuments = useCallback(async () => {
    try { setDocuments(await api.getDocuments(lead.id)); } catch (e) { /* silent */ }
  }, [lead.id]);

  const loadLogoInfo = useCallback(async () => {
    try { setLogoInfo(await api.getLogoInfo(lead.id)); } catch (e) { /* silent */ }
  }, [lead.id]);

  useEffect(() => { loadDocuments(); loadLogoInfo(); }, [loadDocuments, loadLogoInfo]);

  const handleUploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      await api.uploadLogo(lead.id, file);
      showToast('Company logo uploaded');
      setLogoTimestamp(Date.now());
      loadLogoInfo();
    } catch (err) { showToast(err.message, 'error'); }
    setUploadingLogo(false);
    e.target.value = '';
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('Remove the company logo?')) return;
    try {
      await api.deleteLogo(lead.id);
      showToast('Company logo removed');
      setLogoInfo({ exists: false });
    } catch (err) { showToast(err.message, 'error'); }
  };

  const handleUploadDocs = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      await api.uploadDocuments(lead.id, files);
      showToast(`${files.length} document${files.length > 1 ? 's' : ''} uploaded`);
      loadDocuments();
      onRefresh();
    } catch (err) { showToast(err.message, 'error'); }
    setUploading(false);
    e.target.value = ''; // reset input
  };

  const handleDeleteDoc = async (doc) => {
    if (!window.confirm(`Delete "${doc.filename}"?`)) return;
    try {
      await api.deleteDocument(lead.id, doc.id);
      showToast('Document removed');
      loadDocuments();
      onRefresh();
    } catch (err) { showToast(err.message, 'error'); }
  };

  useEffect(() => { setForm(lead); }, [lead]);

  const saveLead = async () => {
    try {
      await api.updateLead(lead.id, form);
      showToast('Lead updated');
      setEditing(false);
      onRefresh();
    } catch (e) { showToast(e.message, 'error'); }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      await api.createActivity({ LeadId: lead.id, type: noteType, description: newNote });
      setNewNote('');
      showToast('Activity logged');
      onRefresh();
    } catch (e) { showToast(e.message, 'error'); }
  };

  const advanceStage = async () => {
    const idx = STAGES.findIndex(s => s.key === lead.stage);
    if (idx < STAGES.length - 1) {
      const next = STAGES[idx + 1].key;
      await api.updateLead(lead.id, { stage: next });
      showToast(`Moved to ${STAGES[idx + 1].label}`);
      onRefresh();
    }
  };

  const deleteLead = async () => {
    if (!window.confirm(`Delete ${lead.companyName}? This cannot be undone.`)) return;
    await api.deleteLead(lead.id);
    showToast('Lead deleted');
    onBack();
  };

  const renderField = (label, field, type = 'text') => (
    <div key={field} style={{ marginBottom: 12 }}>
      <label style={css.label}>{label}</label>
      {editing ? (
        <input style={css.input} type={type} value={form[field] || ''}
          onChange={e => setForm(prev => ({ ...prev, [field]: e.target.value }))} />
      ) : (
        <div style={{ fontSize: 14, color: theme.dark }}>{lead[field] || '—'}</div>
      )}
    </div>
  );

  return (
    <>
      <div style={css.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button style={css.btn('outline')} onClick={onBack}>← Back</button>
          <div>
            <div style={css.headerTitle}>{lead.companyName}</div>
            <div style={{ fontSize: 13, color: theme.gray }}>
              {lead.contactName}
              {lead.franchisee && <span style={{ marginLeft: 12, fontSize: 11, color: theme.primary, fontWeight: 600 }}>
                {lead.franchisee.name}
              </span>}
              {lead.owner && <span style={{ marginLeft: 12, fontSize: 11, color: theme.gray }}>
                Created by {lead.owner.firstName} {lead.owner.lastName}
              </span>}
              {lead.assignee && <span style={{ marginLeft: 12, fontSize: 11, color: '#8B5CF6', fontWeight: 600 }}>
                Assigned to {lead.assignee.firstName} {lead.assignee.lastName}
              </span>}
            </div>
          </div>
          <span style={{ ...css.badge(stage.color), marginLeft: 8 }}>{stage.label}</span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {!canEdit && <span style={{ fontSize: 11, color: theme.gray, fontStyle: 'italic', marginRight: 4 }}>Read-only</span>}
          {canEdit && <button style={css.btn('primary')} onClick={() => onSendEmail()}>Send Email</button>}
          {canEdit && <button style={css.btn('outline')} onClick={advanceStage}>Advance Stage →</button>}
          {canEdit && !editing && (
            <button style={css.btn('outline')} onClick={() => setEditing(true)}>Edit</button>
          )}
          {canEdit && editing && (
            <>
              <button style={css.btn('primary')} onClick={saveLead}>Save</button>
              <button style={css.btn('outline')} onClick={() => { setEditing(false); setForm(lead); }}>Cancel</button>
            </>
          )}
          {isManager && (
            <button style={css.btn('danger')} onClick={deleteLead}>Delete</button>
          )}
        </div>
      </div>

      <div style={css.content}>
        {/* Stage progress bar */}
        <div style={{ ...css.card, padding: 16, marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {STAGES.map((s, i) => {
              const current = STAGES.findIndex(x => x.key === lead.stage);
              const done = i <= current;
              return (
                <React.Fragment key={s.key}>
                  <div style={{
                    flex: 1, height: 6, borderRadius: 3,
                    background: done ? s.color : theme.border,
                    transition: 'background 0.3s',
                  }} title={s.label} />
                </React.Fragment>
              );
            })}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
            <span style={{ fontSize: 10, color: theme.gray }}>New Lead</span>
            <span style={{ fontSize: 10, color: theme.gray }}>Active</span>
          </div>
        </div>

        <div style={css.card}>
          {sectionHeader('company', 'Contact Information & Employer Details')}
          {!collapsed.company && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
              {/* Contact info */}
              <div>
                <h4 style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 700, color: theme.gray, textTransform: 'uppercase', letterSpacing: 0.5 }}>Contact Information</h4>
                {renderField("Contact Name", "contactName")}
                {renderField("Company Name", "companyName")}
                {renderField("Email", "email", "email")}
                {renderField("Phone", "phone", "tel")}
                {renderField("Source", "source")}
                {renderField("Follow-up Date", "followUpDate", "date")}

                {editing ? (
                  <>
                    <div style={{ marginBottom: 12 }}>
                      <label style={css.label}>Stage</label>
                      <select style={{ ...css.select, width: '100%' }} value={form.stage}
                        onChange={e => setForm({ ...form, stage: e.target.value })}>
                        {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                      </select>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={css.label}>Franchisee</label>
                      <select style={{ ...css.select, width: '100%' }} value={form.FranchiseeId || ''}
                        onChange={e => setForm({ ...form, FranchiseeId: e.target.value ? parseInt(e.target.value) : null })}>
                        <option value="">— No Franchisee —</option>
                        {franchisees.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                      </select>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={css.label}>Primary Clinic</label>
                      <select style={{ ...css.select, width: '100%' }} value={form.primaryClinicId || ''}
                        onChange={e => setForm({ ...form, primaryClinicId: e.target.value ? parseInt(e.target.value) : null })}>
                        <option value="">— No Primary Clinic —</option>
                        {franchisees.map(f => (
                          (f.clinics || []).length > 0 && (
                            <optgroup key={f.id} label={f.name}>
                              {(f.clinics || []).map(c => <option key={c.id} value={c.id}>{c.clinicName}</option>)}
                            </optgroup>
                          )
                        ))}
                      </select>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={css.label}>Assigned To</label>
                      <select style={{ ...css.select, width: '100%' }} value={form.assignedTo || ''}
                        onChange={e => setForm({ ...form, assignedTo: e.target.value || null })}>
                        <option value="">— Unassigned —</option>
                        {assignableUsers.map(u => <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>)}
                      </select>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ marginBottom: 12 }}>
                      <label style={css.label}>Franchisee</label>
                      <div style={{ fontSize: 14, color: theme.dark }}>{lead.franchisee ? lead.franchisee.name : '—'}</div>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={css.label}>Primary Clinic</label>
                      <div style={{ fontSize: 14, color: theme.dark }}>{lead.primaryClinic ? lead.primaryClinic.clinicName : '—'}</div>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={css.label}>Assigned To</label>
                      <div style={{ fontSize: 14, color: theme.dark }}>
                        {lead.assignee ? `${lead.assignee.firstName} ${lead.assignee.lastName}` : '— Unassigned —'}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Employer details */}
              <div>
                <h4 style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 700, color: theme.gray, textTransform: 'uppercase', letterSpacing: 0.5 }}>Employer Details</h4>
                <p style={{ fontSize: 12, color: theme.gray, marginTop: 0, marginBottom: 16 }}>Collected via info request email — needed for DocuSign webform.</p>
                {renderField("Legal Company Name", "legalCompanyName")}
                {renderField("Business Address", "businessAddress")}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                  {renderField("City", "businessCity")}
                  {renderField("State", "businessState")}
                  {renderField("ZIP", "businessZip")}
                </div>
                {renderField("Authorized Signatory Name", "authorizedSignatoryName")}
                {renderField("Authorized Signatory Email", "authorizedSignatoryEmail", "email")}
                {renderField("Total Employees", "totalEmployees", "number")}
                {renderField("Industry", "industry")}
                {renderField("Employer Code", "employerCode")}

                {/* Company Logo */}
                <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${theme.border}` }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: theme.gray, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Company Logo</div>
                  {logoInfo?.exists ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{
                        border: `1px solid ${theme.border}`, borderRadius: 6, padding: 10,
                        background: theme.lightGray, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        minHeight: 80,
                      }}>
                        <img
                          src={`${api.getLogoUrl(lead.id)}&t=${logoTimestamp}`}
                          alt="Company logo"
                          style={{ maxHeight: 80, maxWidth: '100%', objectFit: 'contain' }}
                        />
                      </div>
                      <div style={{ fontSize: 11, color: theme.gray }}>
                        {logoInfo.filename}
                        {logoInfo.fileSize ? ` — ${(logoInfo.fileSize / 1024).toFixed(0)} KB` : ''}
                        {logoInfo.updatedAt ? `, updated ${new Date(logoInfo.updatedAt).toLocaleDateString()}` : ''}
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button style={css.btn('outline')} onClick={async () => {
                          try { await api.downloadLogo(lead.id, logoInfo.filename); } catch (e) { showToast(e.message, 'error'); }
                        }}>Download</button>
                        {canEdit && (
                          <>
                            <label style={{ ...css.btn('outline'), cursor: 'pointer', opacity: uploadingLogo ? 0.7 : 1 }}>
                              {uploadingLogo ? 'Uploading...' : 'Replace'}
                              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleUploadLogo} disabled={uploadingLogo} />
                            </label>
                            <button style={{ ...css.btn('outline'), color: theme.danger, borderColor: theme.danger }} onClick={handleDeleteLogo}>Delete</button>
                          </>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ fontSize: 12, color: theme.gray }}>No logo uploaded yet.</div>
                      {canEdit && (
                        <label style={{ ...css.btn('primary'), cursor: 'pointer', alignSelf: 'flex-start', opacity: uploadingLogo ? 0.7 : 1 }}>
                          {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleUploadLogo} disabled={uploadingLogo} />
                        </label>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Process Actions — only for owners/assignees/admins/managers */}
        {canEdit && <div style={{ ...css.card, marginTop: 8 }}>
          {sectionHeader('process', 'Process Actions')}
          {!collapsed.process && (
            <>
              {/* Numbered step actions */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
                {[
                  { num: 1, label: 'Email Initial Interest', desc: 'Introduce the Perk Program and attach the Employer Deck', template: 'initial_interest', color: theme.primary },
                  { num: 2, label: 'Request Registration Info', desc: 'Request details needed to prepare the employer agreement', template: 'registration_info', color: '#8B5CF6' },
                  { num: 3, label: 'Send DocuSign Agreement', desc: 'Open the DocuSign webform to generate and send the agreement', href: DOCUSIGN_URL, color: '#3B82F6', franchiseeOnly: true },
                  { num: 4, label: 'Request Employer Code', desc: 'Submit a helpdesk request for the employer code', href: HELPDESK_CODE_URL, color: '#14B8A6' },
                  { num: 5, label: 'Generate Marketing Materials', desc: 'Open DokShop templates to create customized marketing materials for the employer', action: 'multi_link', links: [
                    { label: 'Email Flyer', href: 'https://thejoint.dokshop.com/Category/25097/Product/136243' },
                    { label: 'Employee Flyer', href: 'https://thejoint.dokshop.com/Category/25097/Product/136266' },
                    { label: 'Employer Launch Kit', href: 'https://thejoint.dokshop.com/Category/25097/Product/173531' },
                  ], color: '#E879F9' },
                  { num: 6, label: 'Upload Marketing Materials', desc: `Upload employer-specific flyers, email copy, and other materials (${documents.length} uploaded)`, action: 'upload_docs', color: '#F59E0B' },
                  { num: 7, label: 'Notify Agreement Executed', desc: 'Notify the employer with welcome info and their marketing materials attached', template: 'agreement_executed', color: '#10B981' },
                  { num: 8, label: 'Check-in', desc: 'Ask for feedback on the program and optionally re-send marketing materials', template: 'check_in', color: '#06B6D4' },
                ].map(step => (
                  <div key={step.num} style={{
                    display: 'flex', alignItems: 'center', gap: 16, padding: '12px 16px',
                    borderRadius: 8, border: `1px solid ${theme.border}`, background: theme.white,
                  }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%', background: step.color,
                      color: theme.white, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 14, fontWeight: 700, flexShrink: 0,
                    }}>{step.num}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: theme.dark }}>{step.label}</div>
                      <div style={{ fontSize: 12, color: theme.gray, marginTop: 2 }}>{step.desc}</div>
                    </div>
                    {step.template ? (
                      <button style={css.btn('primary')} onClick={() => onSendEmail(step.template)}>
                        Send Email
                      </button>
                    ) : step.action === 'upload_docs' ? (
                      <label style={{ ...css.btn('primary'), cursor: 'pointer' }}>
                        Upload
                        <input type="file" multiple style={{ display: 'none' }} onChange={handleUploadDocs}
                          accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.pptx,.xlsx" />
                      </label>
                    ) : step.action === 'multi_link' ? (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {step.links.map(lnk => (
                          <a key={lnk.label} href={lnk.href} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                            <button style={css.btn('outline')}>{lnk.label}</button>
                          </a>
                        ))}
                      </div>
                    ) : (step.franchiseeOnly && !isFranchisee && !user?.isGlobalAdmin) ? (
                      // Non-franchisees still see the step, but Open explains it's franchisee-only.
                      // Global admins are allowed through like franchisees.
                      <button style={css.btn('outline')} onClick={() => setFranchiseeBlock(true)}>Open</button>
                    ) : (
                      <a href={step.href} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                        <button style={css.btn('outline')}>Open</button>
                      </a>
                    )}
                  </div>
                ))}
              </div>

              {/* Additional quick actions */}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <button style={css.btn('outline')} onClick={() => onSendEmail('follow_up')}>Send Follow-up</button>
                <a href={HELPDESK_FEEDBACK_URL} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                  <button style={css.btn('outline')}>Submit Feedback</button>
                </a>
                {lead.email && (
                  <a href={`mailto:${lead.email}`} style={{ textDecoration: 'none' }}>
                    <button style={css.btn('outline')}>Open in Email Client</button>
                  </a>
                )}
              </div>
            </>
          )}
        </div>}

        {/* Notes */}
        <div style={css.card}>
          {sectionHeader('notes', 'Notes')}
          {!collapsed.notes && (
            editing ? (
              <textarea style={{ ...css.input, minHeight: 80 }} value={form.notes || ''}
                onChange={e => setForm({ ...form, notes: e.target.value })} />
            ) : (
              <p style={{ fontSize: 14, color: theme.dark, whiteSpace: 'pre-wrap', margin: 0 }}>{lead.notes || 'No notes.'}</p>
            )
          )}
        </div>

        {/* Activity Log */}
        <div style={css.card}>
          {sectionHeader('activity', 'Activity Log')}
          {!collapsed.activity && (
            <>
              {/* Add activity */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <select style={css.select} value={noteType} onChange={e => setNoteType(e.target.value)}>
                  <option value="note">Note</option>
                  <option value="call">Call</option>
                  <option value="meeting">Meeting</option>
                  <option value="email">Email</option>
                </select>
                <input style={{ ...css.input, flex: 1 }} placeholder="Add an activity..." value={newNote}
                  onChange={e => setNewNote(e.target.value)} onKeyDown={e => e.key === 'Enter' && addNote()} />
                <button style={css.btn('primary')} onClick={addNote}>Add</button>
              </div>

              {/* Activities list */}
              {(!lead.activities || lead.activities.length === 0) ? (
                <p style={{ color: theme.gray, fontSize: 13 }}>No activities yet.</p>
              ) : (
                lead.activities.map(a => {
                  const typeColors = { note: '#6B7280', call: '#3B82F6', email: '#8B5CF6', meeting: '#F59E0B', stage_change: '#10B981', system: '#9CA3AF' };
                  return (
                    <div key={a.id} style={{ padding: '8px 0', borderBottom: `1px solid ${theme.border}`, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <span style={{ ...css.badge(typeColors[a.type] || '#999'), marginTop: 2, textTransform: 'capitalize', minWidth: 60, textAlign: 'center' }}>{a.type}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, color: theme.dark }}>{a.description}</div>
                        <div style={{ fontSize: 11, color: theme.gray }}>{formatDateTime(a.createdAt)}</div>
                      </div>
                    </div>
                  );
                })
              )}
            </>
          )}
        </div>

        {/* Marketing Materials — at bottom */}
        <div style={css.card}>
          {sectionHeader('marketing', 'Marketing Materials',
            canEdit ? (
              <label style={{ ...css.btn('primary'), cursor: 'pointer', opacity: uploading ? 0.7 : 1 }}>
                {uploading ? 'Uploading...' : '+ Upload Files'}
                <input type="file" multiple style={{ display: 'none' }} onChange={handleUploadDocs} disabled={uploading}
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.pptx,.xlsx" />
              </label>
            ) : null
          )}
          {!collapsed.marketing && (
            documents.length === 0 ? (
              <p style={{ color: theme.gray, fontSize: 13, margin: 0 }}>
                No marketing documents uploaded yet. Upload flyers, email copy, or other materials to include with emails to this employer.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {documents.map(doc => (
                  <div key={doc.id} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px',
                    borderRadius: 6, border: `1px solid ${theme.border}`, background: theme.lightGray,
                  }}>
                    <span style={{ fontSize: 18 }}>📄</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: theme.dark }}>{doc.filename}</div>
                      <div style={{ fontSize: 11, color: theme.gray }}>
                        {doc.fileSize ? `${(doc.fileSize / 1024).toFixed(0)} KB` : ''} — {formatDateTime(doc.createdAt)}
                      </div>
                    </div>
                    <button style={{ ...css.btn('outline'), padding: '4px 10px', fontSize: 12 }}
                      onClick={() => {
                        const token = api.getToken();
                        const tokenParam = token ? `?token=${encodeURIComponent(token)}` : '';
                        window.open(api.getDocumentUrl(lead.id, doc.id) + tokenParam, '_blank');
                      }}>
                      Download
                    </button>
                    {canEdit && (
                      <button style={{ ...css.btn('danger'), padding: '4px 10px', fontSize: 12 }}
                        onClick={() => handleDeleteDoc(doc)}>
                        Delete
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>

      {/* DocuSign step is franchisee-only */}
      {franchiseeBlock && (
        <div style={css.modal} onClick={() => setFranchiseeBlock(false)}>
          <div style={{ ...css.modalContent, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 700, color: theme.dark }}>Franchisee only</h2>
            <p style={{ fontSize: 14, color: theme.dark, marginBottom: 20 }}>
              Only franchisee users can create and send the DocuSign agreement. Please reach out to a franchisee on your team to complete this step.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button style={css.btn('primary')} onClick={() => setFranchiseeBlock(false)}>OK</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   PIPELINE (Kanban)
   ═══════════════════════════════════════════════════════════════════ */
function PipelinePage({ leads, onOpenLead, onRefresh, showToast }) {
  const [draggedLeadId, setDraggedLeadId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [updating, setUpdating] = useState(null);

  const handleDragStart = (e, leadId) => {
    setDraggedLeadId(leadId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', leadId.toString());
    // Make the card semi-transparent while dragging
    requestAnimationFrame(() => {
      e.target.style.opacity = '0.4';
    });
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedLeadId(null);
    setDragOverStage(null);
  };

  const handleDragOver = (e, stageKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverStage !== stageKey) setDragOverStage(stageKey);
  };

  const handleDragLeave = (e, stageKey) => {
    // Only clear if actually leaving the column (not entering a child)
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverStage(null);
    }
  };

  const handleDrop = async (e, targetStageKey) => {
    e.preventDefault();
    setDragOverStage(null);
    const leadId = parseInt(e.dataTransfer.getData('text/plain'), 10);
    const lead = leads.find(l => l.id === leadId);
    if (!lead || lead.stage === targetStageKey) return;

    const fromLabel = STAGE_MAP[lead.stage]?.label || lead.stage;
    const toLabel = STAGE_MAP[targetStageKey]?.label || targetStageKey;

    setUpdating(leadId);
    try {
      await api.updateLead(leadId, { stage: targetStageKey });
      showToast(`Moved "${lead.companyName}" from ${fromLabel} to ${toLabel}`);
      onRefresh();
    } catch (err) {
      showToast(err.message || 'Failed to move lead', 'error');
    } finally {
      setUpdating(null);
    }
  };

  return (
    <>
      <div style={css.header}>
        <div style={css.headerTitle}>Pipeline</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 11, color: theme.gray }}>Drag leads between columns to update stage</span>
          <RefreshBtn onClick={onRefresh} />
        </div>
      </div>
      <div style={{ padding: 24, flex: 1, overflow: 'auto' }}>
        <div style={{ display: 'flex', gap: 12, minWidth: STAGES.length * 200, paddingBottom: 16 }}>
          {STAGES.map(s => {
            const stageLeads = leads.filter(l => l.stage === s.key);
            const isOver = dragOverStage === s.key;
            return (
              <div key={s.key} style={{ flex: 1, minWidth: 180, maxWidth: 260 }}>
                <div style={{
                  padding: '8px 12px', borderRadius: '8px 8px 0 0',
                  background: s.color + '15', borderTop: `3px solid ${s.color}`,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: s.color }}>{s.label}</span>
                  <span style={{ ...css.badge(s.color) }}>{stageLeads.length}</span>
                </div>
                <div
                  onDragOver={(e) => handleDragOver(e, s.key)}
                  onDragLeave={(e) => handleDragLeave(e, s.key)}
                  onDrop={(e) => handleDrop(e, s.key)}
                  style={{
                    background: isOver ? s.color + '15' : theme.lightGray,
                    borderRadius: '0 0 8px 8px', padding: 8,
                    minHeight: 120, display: 'flex', flexDirection: 'column', gap: 8,
                    border: isOver ? `2px dashed ${s.color}` : '2px solid transparent',
                    transition: 'background 0.2s, border-color 0.2s',
                  }}
                >
                  {stageLeads.map(l => (
                    <div key={l.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, l.id)}
                      onDragEnd={handleDragEnd}
                      onClick={() => { if (!draggedLeadId) onOpenLead(l.id); }}
                      style={{
                        background: updating === l.id ? theme.lightGray : theme.white,
                        borderRadius: 6, padding: '10px 12px',
                        cursor: draggedLeadId ? 'grabbing' : 'grab',
                        border: `1px solid ${theme.border}`,
                        transition: 'box-shadow 0.15s, opacity 0.15s',
                        opacity: updating === l.id ? 0.5 : 1,
                        userSelect: 'none',
                      }}
                      onMouseEnter={e => { if (!draggedLeadId) e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'; }}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
                      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>{l.companyName}</div>
                      <div style={{ fontSize: 12, color: theme.gray }}>{l.contactName}</div>
                      {l.franchisee && (
                        <div style={{ fontSize: 11, color: theme.primary, fontWeight: 500, marginTop: 2 }}>{l.franchisee.name}</div>
                      )}
                      {l.followUpDate && (
                        <div style={{
                          fontSize: 11, marginTop: 6,
                          color: new Date(l.followUpDate) <= new Date() ? theme.danger : theme.gray,
                          fontWeight: new Date(l.followUpDate) <= new Date() ? 600 : 400,
                        }}>
                          Follow-up: {formatDate(l.followUpDate)}
                        </div>
                      )}
                    </div>
                  ))}
                  {isOver && stageLeads.length === 0 && (
                    <div style={{
                      padding: 16, textAlign: 'center', fontSize: 12, color: s.color,
                      fontWeight: 600, opacity: 0.7,
                    }}>
                      Drop here
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   SETTINGS
   ═══════════════════════════════════════════════════════════════════ */
function SettingsPage({ settings, onSave, showToast, franchisees, onFranchiseesChange, globalDefaults = false, restrictedMode = false }) {
  const [form, setForm] = useState(settings);
  const [testing, setTesting] = useState(false);
  const [pocError, setPocError] = useState('');   // popup when a POC email is taken
  const [savingPoc, setSavingPoc] = useState(false);
  // Franchisee management
  const [editingFran, setEditingFran] = useState(null);  // null=list, 'new'=creating, or franchisee object
  const [franForm, setFranForm] = useState({});
  const [clinicForm, setClinicForm] = useState({});
  const [addingClinic, setAddingClinic] = useState(false);
  const [editingClinicId, setEditingClinicId] = useState(null);
  const [savingFran, setSavingFran] = useState(false);

  useEffect(() => { setForm(settings); }, [settings]);

  const testSmtp = async () => {
    setTesting(true);
    try {
      const res = await api.testSmtp();
      showToast(res.message || 'SMTP connected!');
    } catch (e) { showToast(e.message, 'error'); }
    setTesting(false);
  };

  // Safe save. If the billing POC email is already another company's POC, the
  // server rejects it (409) — show a popup and don't change anything.
  const saveSettings = async (formToSave) => {
    setSavingPoc(true);
    try {
      await onSave(formToSave || form);
    } catch (e) {
      if (e.code === 'POC_EMAIL_TAKEN') setPocError(e.message);
      else showToast(e.message || 'Could not save settings', 'error');
    }
    setSavingPoc(false);
  };

  const renderF = (label, field, type = 'text', placeholder = '') => (
    <div key={field} style={{ marginBottom: 14 }}>
      <label style={css.label}>{label}</label>
      <input style={css.input} type={type} placeholder={placeholder} value={form[field] || ''}
        onChange={e => setForm(prev => ({ ...prev, [field]: e.target.value }))} />
    </div>
  );

  // ── Franchisee CRUD handlers ──
  const startEditFranchisee = (f) => {
    setEditingFran(f);
    setFranForm({ name: f.name, contactName: f.contactName || '', contactEmail: f.contactEmail || '',
      contactTitle: f.contactTitle || '', phone: f.phone || '', street: f.street || '',
      city: f.city || '', state: f.state || '', zip: f.zip || '', legalEntity: f.legalEntity || '' });
    setAddingClinic(false);
    setClinicForm({});
  };

  const startNewFranchisee = () => {
    setEditingFran('new');
    setFranForm({ name: '', contactName: '', contactEmail: '', contactTitle: '', phone: '',
      street: '', city: '', state: '', zip: '', legalEntity: '' });
    setAddingClinic(false);
  };

  const saveFranchisee = async () => {
    if (!franForm.name?.trim()) { showToast('Franchise name is required', 'error'); return; }
    setSavingFran(true);
    try {
      if (editingFran === 'new') {
        const created = await api.createFranchisee(franForm);
        showToast('Franchise created — you can now add clinic locations below');
        await onFranchiseesChange();
        // Auto-switch to edit mode so user can add clinics
        const refreshed = (await api.getFranchisees()).find(f => f.id === created.id);
        if (refreshed) {
          setEditingFran(refreshed);
          setFranForm({ name: refreshed.name, contactName: refreshed.contactName || '', contactEmail: refreshed.contactEmail || '',
            contactTitle: refreshed.contactTitle || '', phone: refreshed.phone || '', street: refreshed.street || '',
            city: refreshed.city || '', state: refreshed.state || '', zip: refreshed.zip || '', legalEntity: refreshed.legalEntity || '' });
        }
      } else {
        await api.updateFranchisee(editingFran.id, franForm);
        showToast('Franchise updated');
        await onFranchiseesChange();
        // Stay in edit mode with refreshed data
        const refreshed = (await api.getFranchisees()).find(f => f.id === editingFran.id);
        if (refreshed) setEditingFran(refreshed);
        else setEditingFran(null);
      }
    } catch (e) { showToast(e.message, 'error'); }
    setSavingFran(false);
  };

  const deleteFranchisee = async (f) => {
    if (!window.confirm(`Delete franchise "${f.name}"? All associated clinics will also be removed.`)) return;
    try {
      await api.deleteFranchisee(f.id);
      showToast('Franchise deleted');
      await onFranchiseesChange();
      if (editingFran?.id === f.id) setEditingFran(null);
    } catch (e) { showToast(e.message, 'error'); }
  };

  const addClinic = async () => {
    if (!clinicForm.clinicName?.trim()) { showToast('Clinic name is required', 'error'); return; }
    try {
      await api.addClinic(editingFran.id, clinicForm);
      showToast('Clinic added');
      setClinicForm({});
      setAddingClinic(false);
      await onFranchiseesChange();
      // Refresh the editing view
      const updated = (await api.getFranchisees()).find(f => f.id === editingFran.id);
      if (updated) setEditingFran(updated);
    } catch (e) { showToast(e.message, 'error'); }
  };

  const startEditClinic = (c) => {
    setEditingClinicId(c.id);
    setClinicForm({ clinicName: c.clinicName || '', clinicNumber: c.clinicNumber || '', clinicAddress: c.clinicAddress || '' });
    setAddingClinic(false);
  };

  const saveClinicEdit = async () => {
    if (!clinicForm.clinicName?.trim()) { showToast('Clinic name is required', 'error'); return; }
    try {
      await api.updateClinic(editingFran.id, editingClinicId, clinicForm);
      showToast('Clinic updated');
      setEditingClinicId(null);
      setClinicForm({});
      await onFranchiseesChange();
      const updated = (await api.getFranchisees()).find(f => f.id === editingFran.id);
      if (updated) setEditingFran(updated);
    } catch (e) { showToast(e.message, 'error'); }
  };

  const deleteClinic = async (clinicId) => {
    if (!window.confirm('Delete this clinic location?')) return;
    try {
      await api.deleteClinic(editingFran.id, clinicId);
      showToast('Clinic removed');
      await onFranchiseesChange();
      const updated = (await api.getFranchisees()).find(f => f.id === editingFran.id);
      if (updated) setEditingFran(updated);
    } catch (e) { showToast(e.message, 'error'); }
  };

  const franField = (label, field, type = 'text', placeholder = '') => (
    <div key={field} style={{ marginBottom: 14 }}>
      <label style={css.label}>{label}</label>
      <input style={css.input} type={type} placeholder={placeholder} value={franForm[field] || ''}
        onChange={e => setFranForm(prev => ({ ...prev, [field]: e.target.value }))} />
    </div>
  );

  const clinicField = (label, field, placeholder = '') => (
    <div key={field} style={{ marginBottom: 10 }}>
      <label style={css.label}>{label}</label>
      <input style={css.input} placeholder={placeholder} value={clinicForm[field] || ''}
        onChange={e => setClinicForm(prev => ({ ...prev, [field]: e.target.value }))} />
    </div>
  );

  return (
    <>
      <div style={css.header}>
        <div style={css.headerTitle}>{globalDefaults ? 'Settings' : 'Franchises and Clinics'}</div>
      </div>
      <div style={css.content}>
        {globalDefaults && (
          <div style={{ ...css.card, marginBottom: 24, background: '#F0FDFA', border: `1px solid ${theme.primary}` }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: theme.dark, marginBottom: 4 }}>Global default settings</div>
            <div style={{ fontSize: 13, color: theme.gray }}>
              For now the only global default is the SMTP server below. Saving it fills in the SMTP settings for any company that hasn't configured its own.
            </div>
          </div>
        )}
        {!globalDefaults && (<>
        {/* Billing Point of Contact moved to the My Plan page. */}

        {/* ─── Franchisees Section (hidden while the subscription is inactive) ─── */}
        {!restrictedMode && (
        <div style={{ ...css.card, marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: theme.dark }}>Franchises & Clinic Locations</h3>
              <p style={{ fontSize: 12, color: theme.gray, margin: '4px 0 0' }}>Manage franchises and their associated clinic locations. Leads can be assigned to a franchise.</p>
            </div>
            <button data-tour="add-franchise" style={css.btn('primary')} onClick={startNewFranchisee}>+ Add Franchise</button>
          </div>

          {editingFran ? (
            /* ── Franchisee Edit/Create Form ── */
            <div style={{ background: theme.lightGray, borderRadius: 8, padding: 24 }}>
              <h4 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 700, color: theme.dark }}>
                {editingFran === 'new' ? 'New Franchise' : `Edit: ${editingFran.name}`}
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                {franField("Franchise / Entity Name *", "name", "text", "e.g., MGH Ventures LLC")}
                {franField("Legal Entity", "legalEntity", "text", "e.g., MGH Ventures LLC")}
                {franField("Contact Name", "contactName", "text", "e.g., John Smith")}
                {franField("Contact Email", "contactEmail", "email", "john@thejoint.com")}
                {franField("Title", "contactTitle", "text", "e.g., Owner")}
                {franField("Phone", "phone", "tel")}
                {franField("Street Address", "street")}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px', gap: 8 }}>
                  {franField("City", "city")}
                  {franField("State", "state")}
                  {franField("ZIP", "zip")}
                </div>
              </div>

              {/* ── Clinic locations (only when editing existing) ── */}
              {editingFran !== 'new' && (
                <div style={{ marginTop: 20, borderTop: `1px solid ${theme.border}`, paddingTop: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: theme.dark }}>Clinic Locations</h4>
                    <button style={{ ...css.btn('outline'), fontSize: 12, padding: '4px 10px' }}
                      onClick={() => { setAddingClinic(true); setClinicForm({}); }}>+ Add Clinic</button>
                  </div>
                  {(editingFran.clinics || []).length === 0 && !addingClinic && (
                    <p style={{ fontSize: 12, color: theme.gray }}>No clinic locations yet. Click "+ Add Clinic" to add one.</p>
                  )}
                  {(editingFran.clinics || []).map(c => (
                    editingClinicId === c.id ? (
                      <div key={c.id} style={{ background: theme.white, borderRadius: 6, border: `1px solid ${theme.primary}`, padding: 12, marginBottom: 6 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0 12px' }}>
                          {clinicField("Clinic Name *", "clinicName", "e.g., The Joint - Lake Tahoe")}
                          {clinicField("Clinic Number", "clinicNumber", "e.g., 0678")}
                        </div>
                        {clinicField("Address", "clinicAddress", "Full address")}
                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                          <button style={{ ...css.btn('primary'), fontSize: 12, padding: '5px 12px' }} onClick={saveClinicEdit}>Save</button>
                          <button style={{ ...css.btn('outline'), fontSize: 12, padding: '5px 12px' }} onClick={() => { setEditingClinicId(null); setClinicForm({}); }}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div key={c.id} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '8px 12px', background: theme.white, borderRadius: 6,
                        border: `1px solid ${theme.border}`, marginBottom: 6,
                      }}>
                        <div>
                          <span style={{ fontWeight: 600, fontSize: 13 }}>{c.clinicName}</span>
                          {c.clinicNumber && <span style={{ fontSize: 12, color: theme.gray, marginLeft: 8 }}>#{c.clinicNumber}</span>}
                          {c.clinicAddress && <div style={{ fontSize: 12, color: theme.gray }}>{c.clinicAddress}</div>}
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button style={{ ...css.btn('outline'), fontSize: 11, padding: '3px 8px' }}
                            onClick={() => startEditClinic(c)}>Edit</button>
                          <button style={{ ...css.btn('danger'), fontSize: 11, padding: '3px 8px' }}
                            onClick={() => deleteClinic(c.id)}>Remove</button>
                        </div>
                      </div>
                    )
                  ))}
                  {addingClinic && (
                    <div style={{ background: theme.white, borderRadius: 6, border: `1px solid ${theme.border}`, padding: 12, marginTop: 8 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0 12px' }}>
                        {clinicField("Clinic Name *", "clinicName", "e.g., The Joint - Lake Tahoe")}
                        {clinicField("Clinic Number", "clinicNumber", "e.g., 0678")}
                      </div>
                      {clinicField("Address", "clinicAddress", "Full address")}
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <button style={{ ...css.btn('primary'), fontSize: 12, padding: '5px 12px' }} onClick={addClinic}>Save Clinic</button>
                        <button style={{ ...css.btn('outline'), fontSize: 12, padding: '5px 12px' }} onClick={() => setAddingClinic(false)}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
                <button style={css.btn('primary')} onClick={saveFranchisee} disabled={savingFran}>
                  {savingFran ? 'Saving...' : (editingFran === 'new' ? 'Create Franchise' : 'Save Changes')}
                </button>
                <button style={css.btn('outline')} onClick={() => setEditingFran(null)}>
                  {editingFran === 'new' ? 'Cancel' : 'Done'}
                </button>
              </div>
            </div>
          ) : (
            /* ── Franchisee List ── */
            franchisees.length === 0 ? (
              <p style={{ fontSize: 13, color: theme.gray, textAlign: 'center', padding: '20px 0' }}>
                No franchises configured yet. Click "+ Add Franchise" to get started.
              </p>
            ) : (
              <table style={css.table}>
                <thead>
                  <tr>
                    <th style={css.th}>Franchise Name</th>
                    <th style={css.th}>Contact</th>
                    <th style={css.th}>Email</th>
                    <th style={css.th}>Clinics</th>
                    <th style={css.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {franchisees.map(f => (
                    <tr key={f.id}>
                      <td style={{ ...css.td, fontWeight: 600 }}>{f.name}</td>
                      <td style={css.td}>{f.contactName || '—'}</td>
                      <td style={{ ...css.td, fontSize: 13 }}>{f.contactEmail || '—'}</td>
                      <td style={css.td}>
                        <span style={css.badge(theme.primary)}>{(f.clinics || []).length} location{(f.clinics || []).length !== 1 ? 's' : ''}</span>
                      </td>
                      <td style={css.td}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button style={{ ...css.btn('outline'), fontSize: 12, padding: '4px 10px' }}
                            onClick={() => startEditFranchisee(f)}>Edit</button>
                          <button style={{ ...css.btn('danger'), fontSize: 12, padding: '4px 10px' }}
                            onClick={() => deleteFranchisee(f)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
        </div>
        )}
        </>)}

        {/* ─── SMTP Settings — GLOBAL ADMIN ONLY. Never rendered for company
             (non-global) admins; they have no visibility into SMTP at all. ─── */}
        {globalDefaults && (
          <div style={css.card}>
            <h3 style={{ margin: '0 0 20px', fontSize: 15, fontWeight: 700, color: theme.dark }}>Email / SMTP Configuration</h3>
            <p style={{ fontSize: 12, color: theme.gray, marginTop: 0, marginBottom: 16 }}>
              Configure the SMTP server (GoDaddy email or other) used to send emails from the CRM.
              This is the global SMTP server used by every company — companies cannot override it.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              {renderF("SMTP Host", "smtpHost", "text", "smtpout.secureserver.net")}
              {renderF("SMTP Port", "smtpPort", "number", "465")}
              {renderF("SMTP Username", "smtpUser", "text", "you@yourdomain.com")}
              {renderF("SMTP Password", "smtpPass", "password")}
              {renderF("From Address", "smtpFrom", "text", "you@yourdomain.com")}
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button style={css.btn('primary')} onClick={() => saveSettings()}>Save SMTP Settings</button>
              <button style={css.btn('outline')} onClick={testSmtp} disabled={testing}>
                {testing ? 'Testing...' : 'Test SMTP Connection'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Billing POC email already-in-use popup */}
      {pocError && (
        <div style={css.modal} onClick={() => setPocError('')}>
          <div style={{ ...css.modalContent, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 700, color: theme.danger }}>Email already in use</h2>
            <p style={{ fontSize: 14, color: theme.dark, marginBottom: 20 }}>{pocError}</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button style={css.btn('primary')} onClick={() => setPocError('')}>OK</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   EMAIL TEMPLATES (admin)
   ═══════════════════════════════════════════════════════════════════ */
function EmailTemplatesPage({ showToast }) {
  const { activeCompanyId } = useAuth();
  const globalView = activeCompanyId === 'all';
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null); // templateKey being edited
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [deckInfo, setDeckInfo] = useState(null);
  const [uploadingDeck, setUploadingDeck] = useState(false);
  const [slickSheetInfo, setSlickSheetInfo] = useState(null);
  const [uploadingSlickSheet, setUploadingSlickSheet] = useState(false);

  const VARIABLE_HELP = [
    { var: '{{contactFirstName}}', desc: 'Lead contact first name' },
    { var: '{{contactLastName}}', desc: 'Lead contact last name' },
    { var: '{{contactName}}', desc: 'Lead contact full name' },
    { var: '{{companyName}}', desc: 'Lead company name' },
    { var: '{{senderFirstName}}', desc: 'Franchisee first name' },
    { var: '{{senderLastName}}', desc: 'Franchisee last name' },
    { var: '{{senderName}}', desc: 'Franchisee full name' },
    { var: '{{senderEmail}}', desc: 'Sender email address' },
    { var: '{{senderTitle}}', desc: 'Franchisee title' },
    { var: '{{clinicName}}', desc: 'Clinic name' },
    { var: '{{titleLine}}', desc: 'Title / clinic name combined' },
    { var: '{{email}}', desc: 'Lead email address' },
    { var: '{{customMessage}}', desc: 'Custom message (follow-up only)' },
    { var: '{{docsSection}}', desc: 'Marketing docs paragraph (auto-inserted)' },
  ];

  const loadTemplates = useCallback(async () => {
    try { setTemplates(await api.getEmailTemplates()); } catch (e) { showToast(e.message, 'error'); }
  }, [showToast]);

  const loadDeckInfo = useCallback(async () => {
    try { setDeckInfo(await api.getEmployerDeck()); } catch (e) { /* silent */ }
  }, []);

  const loadSlickSheetInfo = useCallback(async () => {
    try { setSlickSheetInfo(await api.getSlickSheet()); } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { loadTemplates(); loadDeckInfo(); loadSlickSheetInfo(); }, [loadTemplates, loadDeckInfo, loadSlickSheetInfo]);

  const handleUploadDeck = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDeck(true);
    try {
      await api.uploadEmployerDeck(file);
      showToast('Employer Deck PDF updated');
      loadDeckInfo();
    } catch (err) { showToast(err.message, 'error'); }
    setUploadingDeck(false);
    e.target.value = '';
  };

  const handleResetDeck = async () => {
    if (!window.confirm('Reset the Employer Deck to the original bundled PDF?')) return;
    try {
      await api.resetEmployerDeck();
      showToast('Employer Deck reset to default');
      loadDeckInfo();
    } catch (err) { showToast(err.message, 'error'); }
  };

  const handleUploadSlickSheet = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingSlickSheet(true);
    try {
      await api.uploadSlickSheet(file);
      showToast('Perk Plan Slick Sheet updated');
      loadSlickSheetInfo();
    } catch (err) { showToast(err.message, 'error'); }
    setUploadingSlickSheet(false);
    e.target.value = '';
  };

  const handleResetSlickSheet = async () => {
    if (!window.confirm('Reset the Perk Plan Slick Sheet to the original bundled PDF?')) return;
    try {
      await api.resetSlickSheet();
      showToast('Perk Plan Slick Sheet reset to default');
      loadSlickSheetInfo();
    } catch (err) { showToast(err.message, 'error'); }
  };

  const startEdit = (tpl) => {
    setEditing(tpl.templateKey);
    setForm({ subject: tpl.subject, htmlBody: tpl.htmlBody, textBody: tpl.textBody });
    setPreviewHtml(null);
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm({});
    setPreviewHtml(null);
  };

  const saveTemplate = async () => {
    setSaving(true);
    try {
      await api.updateEmailTemplate(editing, form);
      showToast('Template saved');
      setEditing(null);
      loadTemplates();
    } catch (e) { showToast(e.message, 'error'); }
    setSaving(false);
  };

  const resetTemplate = async (key) => {
    if (!window.confirm('Reset this template to its default content? Your customizations will be lost.')) return;
    try {
      await api.resetEmailTemplate(key);
      showToast('Template reset to default');
      if (editing === key) cancelEdit();
      loadTemplates();
    } catch (e) { showToast(e.message, 'error'); }
  };

  return (
    <>
      <div style={css.header}>
        <div style={css.headerTitle}>{globalView ? 'Email Templates (Global Defaults)' : 'Email Templates'}</div>
      </div>
      <div style={css.content}>
        {globalView && (
          <div style={{ ...css.card, marginBottom: 16, background: '#F0FDFA', border: `1px solid ${theme.primary}` }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: theme.dark, marginBottom: 4 }}>Editing global default templates</div>
            <div style={{ fontSize: 13, color: theme.gray }}>
              Changes here update new companies and any company still using the default. Companies that have customized a template keep their own version.
            </div>
          </div>
        )}
        {/* Variable reference */}
        <div style={{ ...css.card, marginBottom: 16, padding: '12px 16px' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.dark, marginBottom: 8 }}>Template Variables</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {VARIABLE_HELP.map(v => (
              <div key={v.var} style={{ fontSize: 12, color: theme.gray }}>
                <code style={{ background: theme.lightGray, padding: '2px 6px', borderRadius: 4, fontSize: 11, color: theme.primary, fontWeight: 600 }}>{v.var}</code>
                {' '}{v.desc}
              </div>
            ))}
          </div>
        </div>

        {/* Employer Deck PDF */}
        <div style={{ ...css.card, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: theme.dark }}>Employer Deck PDF</div>
              <div style={{ fontSize: 12, color: theme.gray, marginTop: 2 }}>
                Attached to the Initial Interest email.
                {deckInfo?.exists && (
                  <span>
                    {' '}— {deckInfo.isCustom ? 'Custom upload' : 'Default bundled file'}
                    {deckInfo.fileSize ? `, ${(deckInfo.fileSize / 1024).toFixed(0)} KB` : ''}
                    {deckInfo.updatedAt ? `, updated ${new Date(deckInfo.updatedAt).toLocaleDateString()}` : ''}
                  </span>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button style={css.btn('outline')} onClick={async () => {
                try { await api.downloadEmployerDeck(); } catch (e) { showToast(e.message, 'error'); }
              }}>Download Current</button>
              <label style={{ ...css.btn('primary'), cursor: 'pointer', opacity: uploadingDeck ? 0.7 : 1 }}>
                {uploadingDeck ? 'Uploading...' : 'Upload New PDF'}
                <input type="file" accept=".pdf" style={{ display: 'none' }} onChange={handleUploadDeck} disabled={uploadingDeck} />
              </label>
              {deckInfo?.isCustom && (
                <button style={css.btn('outline')} onClick={handleResetDeck}>Reset Default</button>
              )}
            </div>
          </div>
        </div>

        {/* Perk Plan Slick Sheet */}
        <div style={{ ...css.card, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: theme.dark }}>Perk Plan Slick Sheet</div>
              <div style={{ fontSize: 12, color: theme.gray, marginTop: 2 }}>
                Attached to the Initial Interest email alongside the Employer Deck.
                {slickSheetInfo?.exists && (
                  <span>
                    {' '}— {slickSheetInfo.isCustom ? 'Custom upload' : 'Default bundled file'}
                    {slickSheetInfo.fileSize ? `, ${(slickSheetInfo.fileSize / 1024).toFixed(0)} KB` : ''}
                    {slickSheetInfo.updatedAt ? `, updated ${new Date(slickSheetInfo.updatedAt).toLocaleDateString()}` : ''}
                  </span>
                )}
                {slickSheetInfo && !slickSheetInfo.exists && (
                  <span style={{ color: theme.warning }}> — No file uploaded yet. Upload a PDF to include it in Initial Interest emails.</span>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {slickSheetInfo?.exists && (
                <button style={css.btn('outline')} onClick={async () => {
                  try { await api.downloadSlickSheet(); } catch (e) { showToast(e.message, 'error'); }
                }}>Download Current</button>
              )}
              <label style={{ ...css.btn('primary'), cursor: 'pointer', opacity: uploadingSlickSheet ? 0.7 : 1 }}>
                {uploadingSlickSheet ? 'Uploading...' : 'Upload New PDF'}
                <input type="file" accept=".pdf" style={{ display: 'none' }} onChange={handleUploadSlickSheet} disabled={uploadingSlickSheet} />
              </label>
              {slickSheetInfo?.isCustom && (
                <button style={css.btn('outline')} onClick={handleResetSlickSheet}>Reset Default</button>
              )}
            </div>
          </div>
        </div>

        {templates.map(tpl => {
          const isEditing = editing === tpl.templateKey;
          return (
            <div key={tpl.templateKey} style={{ ...css.card, marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isEditing ? 16 : 0 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: theme.dark }}>{tpl.label}</div>
                  <div style={{ fontSize: 12, color: theme.gray, marginTop: 2 }}>{tpl.description}</div>
                  {!isEditing && <div style={{ fontSize: 12, color: theme.dark, marginTop: 4 }}>Subject: <strong>{tpl.subject}</strong></div>}
                </div>
                {!isEditing && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button style={css.btn('primary')} onClick={() => startEdit(tpl)}>Edit</button>
                    <button style={css.btn('outline')} onClick={() => resetTemplate(tpl.templateKey)}>Reset Default</button>
                  </div>
                )}
              </div>

              {isEditing && (
                <div>
                  <div style={{ marginBottom: 14 }}>
                    <label style={css.label}>Subject Line</label>
                    <input style={css.input} value={form.subject}
                      onChange={e => setForm(prev => ({ ...prev, subject: e.target.value }))} />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 14 }}>
                    <div>
                      <label style={css.label}>HTML Body</label>
                      <textarea style={{ ...css.input, minHeight: 300, fontFamily: 'monospace', fontSize: 12 }}
                        value={form.htmlBody}
                        onChange={e => setForm(prev => ({ ...prev, htmlBody: e.target.value }))} />
                    </div>
                    <div>
                      <label style={css.label}>Plain Text Body</label>
                      <textarea style={{ ...css.input, minHeight: 300, fontFamily: 'monospace', fontSize: 12 }}
                        value={form.textBody}
                        onChange={e => setForm(prev => ({ ...prev, textBody: e.target.value }))} />
                    </div>
                  </div>

                  {/* Live preview */}
                  <div style={{ marginBottom: 14 }}>
                    <label style={css.label}>Preview (HTML)</label>
                    <div style={{
                      border: `1px solid ${theme.border}`, borderRadius: 8, padding: 16,
                      maxHeight: 250, overflow: 'auto', background: '#FAFAFA', fontSize: 13,
                    }}>
                      <div style={{ fontWeight: 600, marginBottom: 8 }}>Subject: {form.subject}</div>
                      <div dangerouslySetInnerHTML={{ __html: form.htmlBody }} />
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button style={css.btn('outline')} onClick={cancelEdit}>Cancel</button>
                    <button style={css.btn('primary')} onClick={saveTemplate} disabled={saving}>
                      {saving ? 'Saving...' : 'Save Template'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   BROADCAST EMAIL (admin — send custom email to all users)
   ═══════════════════════════════════════════════════════════════════ */
function BroadcastEmailPage({ showToast }) {
  const { activeCompanyId } = useAuth();
  const globalView = activeCompanyId === 'all';
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [collapsed, setCollapsed] = useState(new Set());   // collapsed company groups (global view)

  // Load recipients on mount
  useEffect(() => {
    api.getBroadcastRecipients().then(users => {
      setRecipients(users);
      setSelectedIds(new Set(users.map(u => u.id)));
    }).catch(() => {});
  }, []);

  // Global view: group users by company. A user in multiple companies appears
  // under each (selection is by user id, so a checkbox reflects that one user).
  const companyGroups = useMemo(() => {
    if (!globalView) return null;
    const map = {};
    for (const u of recipients) {
      for (const m of (u.memberships || [])) {
        if (!map[m.companyId]) map[m.companyId] = { id: m.companyId, name: m.companyName, users: [] };
        map[m.companyId].users.push({ ...u, companyRole: m.role });
      }
    }
    return Object.values(map).sort((a, b) => a.name.localeCompare(b.name));
  }, [recipients, globalView]);

  const toggleRecipient = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleCollapse = (companyId) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(companyId)) next.delete(companyId); else next.add(companyId);
      return next;
    });
  };

  // Select/deselect every user in a company group at once.
  const toggleCompany = (group) => {
    const ids = group.users.map(u => u.id);
    const allSelected = ids.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) ids.forEach(id => next.delete(id));
      else ids.forEach(id => next.add(id));
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(recipients.map(u => u.id)));
  const selectNone = () => setSelectedIds(new Set());
  const selectAdmins = () => {
    const ids = recipients
      .filter(u => globalView ? u.isAdminAnywhere : u.role === 'ADMIN')
      .map(u => u.id);
    setSelectedIds(new Set(ids));
  };
  const selectedCount = selectedIds.size;

  const handleAddFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setAttachments(prev => [...prev, ...files]);
    e.target.value = '';
  };

  const removeAttachment = (idx) => {
    setAttachments(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSend = async () => {
    if (!subject.trim()) { showToast('Subject is required', 'error'); return; }
    if (!body.trim()) { showToast('Email body is required', 'error'); return; }
    if (selectedCount === 0) { showToast('Select at least one recipient', 'error'); return; }
    const attachNote = attachments.length > 0 ? ` with ${attachments.length} attachment(s)` : '';
    if (!window.confirm(`Send this email${attachNote} to ${selectedCount} user(s)?`)) return;

    setSending(true);
    setResult(null);
    try {
      const htmlBody = body.replace(/\n/g, '<br/>');
      const recipientIds = [...selectedIds];
      const res = await api.sendBroadcast({ subject: subject.trim(), htmlBody, textBody: body, attachments, recipientIds });
      setResult(res);
      showToast(res.message);
    } catch (e) { showToast(e.message, 'error'); }
    setSending(false);
  };

  return (
    <>
      <div style={css.header}>
        <div style={css.headerTitle}>Broadcast Email</div>
      </div>
      <div style={css.content}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24 }}>
          {/* Compose */}
          <div>
            <div style={css.card}>
              <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: theme.dark }}>Compose Message</h3>
              <p style={{ fontSize: 12, color: theme.gray, margin: '0 0 16px' }}>
                Select recipients from the panel on the right, then compose your message.
              </p>

              <div style={{ marginBottom: 14 }}>
                <label style={css.label}>Subject *</label>
                <input style={css.input} value={subject} placeholder="e.g., Important Update — PerkPlan CRM"
                  onChange={e => setSubject(e.target.value)} />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={css.label}>Message Body *</label>
                <textarea style={{ ...css.input, minHeight: 200, fontFamily: 'inherit', lineHeight: 1.6 }}
                  value={body} placeholder="Type your message here. Line breaks will be preserved."
                  onChange={e => setBody(e.target.value)} />
              </div>

              {/* Attachments */}
              <div style={{ marginBottom: 14 }}>
                <label style={css.label}>Attachments</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: attachments.length > 0 ? 8 : 0 }}>
                  <label style={{ ...css.btn('outline'), cursor: 'pointer' }}>
                    + Add File
                    <input type="file" multiple style={{ display: 'none' }} onChange={handleAddFiles} />
                  </label>
                  <span style={{ fontSize: 11, color: theme.gray }}>Max 10 MB per file, up to 5 files</span>
                </div>
                {attachments.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {attachments.map((f, i) => (
                      <div key={i} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '6px 10px', borderRadius: 6, background: theme.lightGray,
                        border: `1px solid ${theme.border}`, fontSize: 13,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span>📎</span>
                          <span style={{ fontWeight: 500 }}>{f.name}</span>
                          <span style={{ fontSize: 11, color: theme.gray }}>({(f.size / 1024).toFixed(1)} KB)</span>
                        </div>
                        <button onClick={() => removeAttachment(i)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.danger, fontSize: 14, fontWeight: 700, padding: '0 4px' }}>
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button style={css.btn('primary')} onClick={handleSend} disabled={sending || !subject.trim() || !body.trim() || selectedCount === 0}>
                  {sending ? 'Sending...' : `Send to ${selectedCount} User${selectedCount !== 1 ? 's' : ''}`}
                </button>
                <button style={css.btn('outline')} onClick={() => setShowPreview(!showPreview)}
                  disabled={!body.trim()}>
                  {showPreview ? 'Hide Preview' : 'Preview'}
                </button>
              </div>

              {/* Send result */}
              {result && (
                <div style={{
                  marginTop: 16, padding: 12, borderRadius: 6,
                  background: result.failed > 0 ? '#FEF3C7' : '#D1FAE5',
                  border: `1px solid ${result.failed > 0 ? '#F59E0B' : '#10B981'}`,
                  fontSize: 13,
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>{result.message}</div>
                  {result.firstError && (
                    <div style={{ fontSize: 12, color: theme.danger, marginBottom: 4 }}>
                      Error: {result.firstError}
                    </div>
                  )}
                  {result.failed > 0 && (
                    <div style={{ fontSize: 12, color: theme.danger }}>
                      Failed: {result.errors.map(e => e.email).join(', ')}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Preview */}
            {showPreview && body.trim() && (
              <div style={css.card}>
                <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 700, color: theme.dark }}>Preview</h3>
                <div style={{ padding: 16, border: `1px solid ${theme.border}`, borderRadius: 6, background: theme.lightGray }}>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8, color: theme.dark }}>
                    Subject: {subject || '(no subject)'}
                  </div>
                  <div style={{ fontSize: 14, color: theme.dark, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                    {body}
                  </div>
                  {attachments.length > 0 && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${theme.border}` }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: theme.gray, marginBottom: 4 }}>Attachments ({attachments.length}):</div>
                      {attachments.map((f, i) => (
                        <div key={i} style={{ fontSize: 12, color: theme.dark }}>📎 {f.name}</div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Recipients sidebar */}
          <div>
            <div style={css.card}>
              <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 700, color: theme.dark }}>
                Recipients ({selectedCount} of {recipients.length})
              </h3>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                <button style={{ ...css.btn('outline'), fontSize: 11, padding: '4px 10px' }} onClick={selectAll}>Select All</button>
                <button style={{ ...css.btn('outline'), fontSize: 11, padding: '4px 10px' }} onClick={selectNone}>Select None</button>
                <button style={{ ...css.btn('outline'), fontSize: 11, padding: '4px 10px' }} onClick={selectAdmins}>Select All Admins</button>
              </div>
              {recipients.length === 0 ? (
                <p style={{ fontSize: 12, color: theme.gray }}>No active users found.</p>
              ) : globalView ? (
                <div style={{ maxHeight: 440, overflow: 'auto' }}>
                  {companyGroups.map(group => {
                    const ids = group.users.map(u => u.id);
                    const allSel = ids.every(id => selectedIds.has(id));
                    const someSel = ids.some(id => selectedIds.has(id));
                    const isCollapsed = collapsed.has(group.id);
                    const selCount = ids.filter(id => selectedIds.has(id)).length;
                    return (
                      <div key={group.id} style={{ marginBottom: 6, border: `1px solid ${theme.border}`, borderRadius: 6 }}>
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                          background: theme.lightGray, borderRadius: 6, cursor: 'pointer',
                        }} onClick={() => toggleCollapse(group.id)}>
                          <input type="checkbox" checked={allSel}
                            ref={el => { if (el) el.indeterminate = someSel && !allSel; }}
                            onChange={() => toggleCompany(group)} onClick={e => e.stopPropagation()}
                            style={{ accentColor: theme.primary, flexShrink: 0 }} />
                          <div style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, color: theme.dark }}>
                            {group.name}
                          </div>
                          <span style={{ fontSize: 11, color: theme.gray }}>{selCount}/{group.users.length}</span>
                          <span style={{ fontSize: 11, color: theme.gray, width: 12, textAlign: 'center' }}>{isCollapsed ? '▸' : '▾'}</span>
                        </div>
                        {!isCollapsed && (
                          <div style={{ padding: '2px 10px 6px' }}>
                            {group.users.map(u => (
                              <div key={u.id} onClick={() => toggleRecipient(u.id)} style={{
                                padding: '6px 0', borderBottom: `1px solid ${theme.border}`,
                                display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                                opacity: selectedIds.has(u.id) ? 1 : 0.5,
                              }}>
                                <input type="checkbox" checked={selectedIds.has(u.id)} onChange={() => toggleRecipient(u.id)}
                                  onClick={e => e.stopPropagation()}
                                  style={{ accentColor: theme.primary, flexShrink: 0 }} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 13, fontWeight: 600 }}>{u.firstName} {u.lastName}</div>
                                  <div style={{ fontSize: 12, color: theme.gray, overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.email}</div>
                                </div>
                                <span style={css.badge(
                                  u.companyRole === 'ADMIN' ? theme.danger : u.companyRole === 'MANAGER' ? theme.warning : theme.primary
                                )}>{u.companyRole}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ maxHeight: 400, overflow: 'auto' }}>
                  {recipients.map(u => (
                    <div key={u.id} onClick={() => toggleRecipient(u.id)} style={{
                      padding: '8px 0', borderBottom: `1px solid ${theme.border}`,
                      display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                      opacity: selectedIds.has(u.id) ? 1 : 0.5,
                    }}>
                      <input type="checkbox" checked={selectedIds.has(u.id)} onChange={() => toggleRecipient(u.id)}
                        onClick={e => e.stopPropagation()}
                        style={{ accentColor: theme.primary, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{u.firstName} {u.lastName}</div>
                        <div style={{ fontSize: 12, color: theme.gray, overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.email}</div>
                      </div>
                      <span style={css.badge(
                        u.role === 'ADMIN' ? theme.danger : u.role === 'MANAGER' ? theme.warning : theme.primary
                      )}>{u.role}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   LEAD MODAL (create / edit)
   ═══════════════════════════════════════════════════════════════════ */
function LeadModal({ lead, franchisees = [], assignableUsers = [], onClose, onSave }) {
  const [form, setForm] = useState(lead || {
    contactName: '', companyName: '', phone: '', email: '', source: '', stage: 'new_lead', notes: '', followUpDate: '', FranchiseeId: '', assignedTo: '',
  });
  const [saving, setSaving] = useState(false);

  const renderF = (label, field, type = 'text', required = false) => (
    <div key={field} style={{ marginBottom: 14 }}>
      <label style={css.label}>{label}{required && ' *'}</label>
      <input style={css.input} type={type} value={form[field] || ''} required={required}
        onChange={e => setForm(prev => ({ ...prev, [field]: e.target.value }))} />
    </div>
  );

  return (
    <div style={css.modal} onClick={onClose}>
      <div style={css.modalContent} onClick={e => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 24px', fontSize: 18, fontWeight: 700, color: theme.dark }}>
          {lead ? 'Edit Lead' : 'New Lead'}
        </h2>
        {renderF("Contact Name", "contactName", "text", true)}
        {renderF("Company Name", "companyName", "text", true)}
        {renderF("Email", "email", "email", true)}
        {renderF("Phone", "phone", "tel")}
        <div style={{ marginBottom: 14 }}>
          <label style={css.label}>Franchisee *</label>
          <select style={{ ...css.select, width: '100%' }} value={form.FranchiseeId || ''} required
            onChange={e => setForm({ ...form, FranchiseeId: e.target.value ? parseInt(e.target.value) : null })}>
            <option value="">— Select Franchisee —</option>
            {franchisees.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={css.label}>Primary Clinic</label>
          <select style={{ ...css.select, width: '100%' }} value={form.primaryClinicId || ''}
            onChange={e => setForm({ ...form, primaryClinicId: e.target.value ? parseInt(e.target.value) : null })}>
            <option value="">— No Primary Clinic —</option>
            {franchisees.map(f => (
              (f.clinics || []).length > 0 && (
                <optgroup key={f.id} label={f.name}>
                  {(f.clinics || []).map(c => <option key={c.id} value={c.id}>{c.clinicName}</option>)}
                </optgroup>
              )
            ))}
          </select>
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={css.label}>Assigned To</label>
          <select style={{ ...css.select, width: '100%' }} value={form.assignedTo || ''}
            onChange={e => setForm({ ...form, assignedTo: e.target.value || null })}>
            <option value="">— Unassigned —</option>
            {assignableUsers.map(u => <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>)}
          </select>
        </div>
        {renderF("Source", "source")}
        {renderF("Follow-up Date", "followUpDate", "date")}
        <div style={{ marginBottom: 14 }}>
          <label style={css.label}>Stage</label>
          <select style={{ ...css.select, width: '100%' }} value={form.stage}
            onChange={e => setForm({ ...form, stage: e.target.value })}>
            {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={css.label}>Notes</label>
          <textarea style={{ ...css.input, minHeight: 60 }} value={form.notes || ''}
            onChange={e => setForm({ ...form, notes: e.target.value })} />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button style={css.btn('outline')} onClick={onClose}>Cancel</button>
          <button style={css.btn('primary')} onClick={async () => {
              if (saving) return;
              setSaving(true);
              try { await onSave(form); } finally { setSaving(false); }
            }}
            disabled={!form.contactName || !form.companyName || !form.email || !form.FranchiseeId || saving}>
            {saving ? 'Saving...' : (lead ? 'Save Changes' : 'Create Lead')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   EMAIL MODAL
   ═══════════════════════════════════════════════════════════════════ */
function EmailModal({ lead, initialTemplate, onClose, showToast, onSent }) {
  const [template, setTemplate] = useState(initialTemplate || 'initial_interest');
  const [customMessage, setCustomMessage] = useState('');
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [useMailto, setUseMailto] = useState(false);

  const loadPreview = async () => {
    try {
      const p = await api.previewEmail({ leadId: lead.id, template, customMessage, includeLeadDocs: true });
      setPreview(p);
    } catch (e) { showToast(e.message, 'error'); }
  };

  useEffect(() => { loadPreview(); }, [template]); // eslint-disable-line

  const hasAttachments = preview?.attachments && preview.attachments.length > 0;

  const downloadAttachment = (attachment) => {
    const token = api.getToken();
    const tokenParam = token ? `?token=${encodeURIComponent(token)}` : '';
    if (attachment.leadDocId) {
      // Lead-specific marketing document
      window.open(api.getDocumentUrl(lead.id, attachment.leadDocId) + tokenParam, '_blank');
    } else {
      // Static asset (e.g., Employer Deck) — use the real file name on disk
      const API = process.env.REACT_APP_API_URL || '/api';
      const file = attachment.assetFile || attachment.filename;
      window.open(`${API}/leads/assets/${encodeURIComponent(file)}${tokenParam}`, '_blank');
    }
  };

  const send = async () => {
    if (useMailto) {
      // Use mailto: link — note: mailto: does NOT support attachments
      const body = preview?.text?.replace(/\n/g, '%0A') || '';
      const subject = encodeURIComponent(preview?.subject || '');
      window.open(`mailto:${lead.email}?subject=${subject}&body=${body}`);

      if (hasAttachments) {
        showToast('Email opened — attachments must be added manually in your email client', 'warning');
      } else {
        showToast('Opened in email client');
      }
      onSent();
      return;
    }

    setSending(true);
    try {
      await api.sendEmail({ leadId: lead.id, template, customMessage, includeLeadDocs: true });
      showToast('Email sent successfully!');
      onSent();
    } catch (e) {
      showToast(e.message, 'error');
    }
    setSending(false);
  };

  const templates = [
    { key: 'initial_interest', label: 'Initial Interest', desc: 'Introduce the Perk Program with the Employer Deck attached' },
    { key: 'registration_info', label: 'Registration Info', desc: 'Request details needed to prepare the employer agreement' },
    { key: 'follow_up', label: 'Follow Up', desc: 'Follow up with a prospective employer' },
    { key: 'agreement_executed', label: 'Agreement Executed', desc: 'Welcome email with marketing materials attached' },
    { key: 'check_in', label: 'Check-in', desc: 'Ask for program feedback, optionally re-send marketing materials' },
  ];

  return (
    <div style={css.modal} onClick={onClose}>
      <div style={{ ...css.modalContent, maxWidth: 700 }} onClick={e => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: theme.dark }}>
          Send Email to {lead.contactName}
        </h2>
        <div style={{ fontSize: 13, color: theme.gray, marginBottom: 16 }}>To: {lead.email || '(no email set)'}</div>

        {/* Template select */}
        <div style={{ marginBottom: 16 }}>
          <label style={css.label}>Email Template</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {templates.map(t => (
              <div key={t.key} onClick={() => setTemplate(t.key)}
                style={{
                  padding: '10px 14px', borderRadius: 8, cursor: 'pointer', flex: 1, minWidth: 160,
                  border: `2px solid ${template === t.key ? theme.primary : theme.border}`,
                  background: template === t.key ? theme.primary + '10' : theme.white,
                }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: template === t.key ? theme.primary : theme.dark }}>{t.label}</div>
                <div style={{ fontSize: 11, color: theme.gray, marginTop: 2 }}>{t.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Custom message for follow-up */}
        {template === 'follow_up' && (
          <div style={{ marginBottom: 16 }}>
            <label style={css.label}>Custom Message (optional — replaces default body)</label>
            <textarea style={{ ...css.input, minHeight: 60 }} value={customMessage}
              onChange={e => { setCustomMessage(e.target.value); }}
              placeholder="Write a custom follow-up message..." />
            <button style={{ ...css.btn('outline'), marginTop: 8, fontSize: 12 }} onClick={loadPreview}>Update Preview</button>
          </div>
        )}

        {/* Preview */}
        {preview && (
          <div style={{ marginBottom: 16 }}>
            <label style={css.label}>Preview</label>
            <div style={{
              border: `1px solid ${theme.border}`, borderRadius: 8, padding: 16,
              maxHeight: 250, overflow: 'auto', background: '#FAFAFA', fontSize: 13,
            }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Subject: {preview.subject}</div>
              {hasAttachments && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6,
                    padding: '8px 10px', background: '#EFF6FF', borderRadius: 6, fontSize: 12, color: '#3B82F6',
                  }}>
                    <span>📎</span>
                    <span style={{ fontWeight: 600 }}>Attachments:</span>
                    {preview.attachments.map((a, i) => (
                      <span key={i} style={{ fontWeight: 600 }}>{a.filename}{i < preview.attachments.length - 1 ? ',' : ''}</span>
                    ))}
                  </div>
                  {useMailto && (
                    <div style={{
                      padding: '8px 10px', background: '#FEF3C7', borderRadius: 6, fontSize: 12,
                      color: '#92400E', marginTop: 6,
                    }}>
                      <strong>Note:</strong> The mailto: protocol does not support attachments. These files will not be attached when opening your email client.
                      You can download them below to attach manually.
                      <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                        {preview.attachments.map((a, i) => (
                          <button key={i} style={{ ...css.btn('outline'), padding: '3px 10px', fontSize: 11 }}
                            onClick={() => downloadAttachment(a)}>
                            Download {a.filename}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div dangerouslySetInnerHTML={{ __html: preview.html }} />
            </div>
          </div>
        )}

        {/* Send method toggle */}
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: theme.dark, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={useMailto} onChange={e => setUseMailto(e.target.checked)} />
            Use mailto: link (opens email client instead of sending via SMTP)
          </label>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button style={css.btn('outline')} onClick={onClose}>Cancel</button>
          <button style={css.btn('primary')} onClick={send} disabled={sending || !lead.email}>
            {sending ? 'Sending...' : useMailto ? 'Open in Email Client' : 'Send Email'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   LOGIN PAGE
   ═══════════════════════════════════════════════════════════════════ */
function LoginPage({ onLogin }) {
  const [mode, setMode] = useState('login'); // 'login' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await onLogin(email, password);
    } catch (err) {
      setError(err.message || 'Invalid credentials');
    }
    setLoading(false);
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError(''); setInfo('');
    setLoading(true);
    try {
      const res = await api.forgotPassword(email);
      setInfo(res.message || 'If an account exists with that email, a password reset link has been sent.');
    } catch (err) {
      setError(err.message || 'Something went wrong');
    }
    setLoading(false);
  };

  const switchMode = (m) => { setMode(m); setError(''); setInfo(''); };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100vh', background: '#111827',
    }}>
      <div style={{
        background: theme.white, borderRadius: 12, padding: 40, width: 400,
        boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: theme.dark }}>
            PerkPlan <span style={{ color: theme.primary }}>CRM</span>
          </div>
          <div style={{ fontSize: 13, color: theme.gray, marginTop: 4 }}>The Joint Chiropractic</div>
        </div>

        {error && (
          <div style={{
            background: '#FEF2F2', color: theme.danger, padding: '10px 14px',
            borderRadius: 6, fontSize: 13, marginBottom: 16, fontWeight: 500,
          }}>
            {error}
          </div>
        )}
        {info && (
          <div style={{
            background: '#ECFDF5', color: '#047857', padding: '10px 14px',
            borderRadius: 6, fontSize: 13, marginBottom: 16, fontWeight: 500,
          }}>
            {info}
          </div>
        )}

        {mode === 'login' ? (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 16 }}>
              <label style={css.label}>Email</label>
              <input style={css.input} type="email" value={email} required
                onChange={e => setEmail(e.target.value)} placeholder="you@example.com" />
            </div>
            <div style={{ marginBottom: 8 }}>
              <label style={css.label}>Password</label>
              <input style={css.input} type="password" value={password} required
                onChange={e => setPassword(e.target.value)} placeholder="Enter your password" />
            </div>
            <div style={{ textAlign: 'right', marginBottom: 20 }}>
              <span onClick={() => switchMode('forgot')}
                style={{ fontSize: 12, color: theme.primary, cursor: 'pointer', fontWeight: 600 }}>
                Forgot password?
              </span>
            </div>
            <button type="submit" disabled={loading}
              style={{
                ...css.btn('primary'), width: '100%', padding: '12px 0',
                justifyContent: 'center', fontSize: 15, opacity: loading ? 0.7 : 1,
              }}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
            <div style={{ textAlign: 'center', marginTop: 20, borderTop: `1px solid ${theme.border}`, paddingTop: 16 }}>
              <div style={{ fontSize: 12, color: theme.gray, marginBottom: 10 }}>Don't have an account?</div>
              <a href="/signup"
                style={{
                  ...css.btn('outline'), width: '100%', padding: '12px 0', boxSizing: 'border-box',
                  justifyContent: 'center', fontSize: 15, textDecoration: 'none', display: 'block', textAlign: 'center',
                }}>
                Register
              </a>
            </div>
          </form>
        ) : (
          <form onSubmit={handleForgot}>
            <div style={{ fontSize: 13, color: theme.gray, marginBottom: 16 }}>
              Enter your account email and we'll send you a link to reset your password.
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={css.label}>Email</label>
              <input style={css.input} type="email" value={email} required
                onChange={e => setEmail(e.target.value)} placeholder="you@example.com" />
            </div>
            <button type="submit" disabled={loading}
              style={{
                ...css.btn('primary'), width: '100%', padding: '12px 0',
                justifyContent: 'center', fontSize: 15, opacity: loading ? 0.7 : 1,
              }}>
              {loading ? 'Sending...' : 'Send reset link'}
            </button>
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <span onClick={() => switchMode('login')}
                style={{ fontSize: 12, color: theme.primary, cursor: 'pointer', fontWeight: 600 }}>
                ← Back to sign in
              </span>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   SIGNUP PAGE (public self-serve, reached via /?signup external link)
   ═══════════════════════════════════════════════════════════════════ */
function SignupPage({ onSignup }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ companyName: '', ein: '', firstName: '', lastName: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try { await onSignup(form); }
    catch (err) { setError(err.message || 'Signup failed'); setLoading(false); }
  };

  const PRICING = [
    { title: 'Monthly', price: '$27.50', unit: '/mo', desc: '$7.50 per additional user' },
    { title: 'Yearly', price: '$231', unit: '/yr', desc: '$63 per additional user' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: theme.lightGray, padding: '48px 20px', overflow: 'auto' }}>
      <div style={{ maxWidth: 820, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 30, fontWeight: 800, color: theme.dark }}>
            PerkPlan <span style={{ color: theme.primary }}>CRM</span>
          </div>
          <div style={{ fontSize: 14, color: theme.gray, marginTop: 6 }}>The Joint Chiropractic</div>
        </div>

        {/* Pricing */}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
          {PRICING.map(p => (
            <div key={p.title} style={{ ...css.card, flex: 1, minWidth: 240 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: theme.dark }}>{p.title}</div>
              <div style={{ margin: '8px 0' }}>
                <span style={{ fontSize: 30, fontWeight: 800, color: theme.dark }}>{p.price}</span>
                <span style={{ fontSize: 14, color: theme.gray }}> {p.unit} for 1 user</span>
              </div>
              <div style={{ fontSize: 13, color: theme.gray }}>{p.desc}</div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 13, color: theme.primary, fontWeight: 600, textAlign: 'center', margin: '12px 0 24px' }}>
          Start with a 30-day free trial — unlimited users, no charge until it ends.
        </div>

        {!showForm ? (
          <div style={{ textAlign: 'center' }}>
            <button style={{ ...css.btn('primary'), padding: '14px 32px', fontSize: 16 }} onClick={() => setShowForm(true)}>
              Sign Up
            </button>
          </div>
        ) : (
          <div style={{ ...css.card, maxWidth: 480, margin: '0 auto' }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: theme.dark }}>Create your account</h3>
            <p style={{ fontSize: 13, color: theme.gray, margin: '0 0 16px' }}>
              Set up your company and admin account. Your 30-day free trial starts right away.
            </p>
            {error && (
              <div style={{ background: '#FEF2F2', color: theme.danger, padding: '10px 14px', borderRadius: 6, fontSize: 13, marginBottom: 16, fontWeight: 500 }}>{error}</div>
            )}
            <form onSubmit={submit}>
              <div style={{ marginBottom: 14 }}>
                <label style={css.label}>Company Name *</label>
                <input style={css.input} value={form.companyName} required
                  onChange={e => set('companyName', e.target.value)} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={css.label}>EIN *</label>
                <input style={css.input} value={form.ein} required
                  inputMode="numeric" placeholder="9-digit number"
                  maxLength={9}
                  onChange={e => set('ein', e.target.value.replace(/\D/g, '').slice(0, 9))} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                <div style={{ marginBottom: 14 }}>
                  <label style={css.label}>First Name *</label>
                  <input style={css.input} value={form.firstName} required onChange={e => set('firstName', e.target.value)} />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={css.label}>Last Name *</label>
                  <input style={css.input} value={form.lastName} required onChange={e => set('lastName', e.target.value)} />
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={css.label}>Email *</label>
                <input style={css.input} type="email" value={form.email} required placeholder="you@example.com"
                  onChange={e => set('email', e.target.value)} />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={css.label}>Password *</label>
                <input style={css.input} type="password" value={form.password} required placeholder="At least 6 characters"
                  onChange={e => set('password', e.target.value)} />
              </div>
              <button type="submit" disabled={loading}
                style={{ ...css.btn('primary'), width: '100%', padding: '12px 0', justifyContent: 'center', fontSize: 15, opacity: loading ? 0.7 : 1 }}>
                {loading ? 'Creating your account…' : 'Create account & start free trial'}
              </button>
            </form>
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <a href="/" style={{ fontSize: 12, color: theme.primary, fontWeight: 600, textDecoration: 'none' }}>
                Already have an account? Sign in
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   RESET PASSWORD PAGE (reached via the emailed link: /?resetToken=...)
   ═══════════════════════════════════════════════════════════════════ */
function ResetPasswordPage({ token }) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const goToLogin = () => { window.location.href = window.location.pathname; };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      await api.resetPassword(token, newPassword);
      setDone(true);
    } catch (err) {
      setError(err.message || 'Invalid or expired reset link');
    }
    setLoading(false);
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100vh', background: '#111827',
    }}>
      <div style={{
        background: theme.white, borderRadius: 12, padding: 40, width: 400,
        boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: theme.dark }}>
            PerkPlan <span style={{ color: theme.primary }}>CRM</span>
          </div>
          <div style={{ fontSize: 13, color: theme.gray, marginTop: 4 }}>Reset your password</div>
        </div>

        {error && (
          <div style={{
            background: '#FEF2F2', color: theme.danger, padding: '10px 14px',
            borderRadius: 6, fontSize: 13, marginBottom: 16, fontWeight: 500,
          }}>
            {error}
          </div>
        )}

        {done ? (
          <div>
            <div style={{
              background: '#ECFDF5', color: '#047857', padding: '10px 14px',
              borderRadius: 6, fontSize: 13, marginBottom: 20, fontWeight: 500,
            }}>
              Your password has been reset. You can now sign in with your new password.
            </div>
            <button onClick={goToLogin}
              style={{ ...css.btn('primary'), width: '100%', padding: '12px 0', justifyContent: 'center', fontSize: 15 }}>
              Go to sign in
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 16 }}>
              <label style={css.label}>New Password</label>
              <input style={css.input} type="password" value={newPassword} required
                onChange={e => setNewPassword(e.target.value)} placeholder="At least 6 characters" />
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={css.label}>Confirm New Password</label>
              <input style={css.input} type="password" value={confirmPassword} required
                onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter new password" />
            </div>
            <button type="submit" disabled={loading}
              style={{ ...css.btn('primary'), width: '100%', padding: '12px 0', justifyContent: 'center', fontSize: 15, opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <span onClick={goToLogin}
                style={{ fontSize: 12, color: theme.primary, cursor: 'pointer', fontWeight: 600 }}>
                ← Back to sign in
              </span>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/* ─── Stripe payment form: card OR US bank account (rendered in <Elements>) ─── */
function PaymentForm({ amountLabel, trialEndLabel, onPaid, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Deferred = free-trial advance payment (charged later); otherwise charged now.
  const deferred = !!trialEndLabel;

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true); setError('');
    // We always confirm a SetupIntent to SAVE the payment method — no invoice
    // exists yet. The subscription (and its first charge) is created afterward
    // in onPaid, so backing out here charges nothing and creates no invoice.
    const { error: err, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.origin + window.location.pathname },
      redirect: 'if_required',
    });
    if (err) {
      setError(err.message || 'Could not save payment method');
      setSubmitting(false);
      return;
    }
    try {
      await onPaid(setupIntent ? setupIntent.status : 'succeeded', setupIntent);
    } catch (e) {
      setError(e.message || 'Could not finalize');
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div style={{ fontSize: 14, color: theme.dark, marginBottom: 12 }}>
        {deferred
          ? <>Save a payment method by card or US bank account. You won't be charged until your trial ends on {trialEndLabel}.</>
          : <>Enter a payment method by card or US bank account to start your subscription{amountLabel ? <> — you'll be charged <strong>{amountLabel}</strong></> : ''}.</>}
      </div>
      <div style={{ marginBottom: 12 }}>
        <PaymentElement />
      </div>
      {error && (
        <div style={{ background: '#FEF2F2', color: theme.danger, padding: '8px 12px', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>{error}</div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <button style={css.btn('outline')} disabled={submitting} onClick={onCancel}>Cancel</button>
        <button style={css.btn('primary')} disabled={submitting || !stripe} onClick={handlePay}>
          {submitting ? 'Processing...' : (deferred ? 'Save payment method' : 'Start subscription')}
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   MY PLAN PAGE (billing)
   ═══════════════════════════════════════════════════════════════════ */
function PlanPage({ showToast, settings = {}, reloadSettings }) {
  const { user } = useAuth();
  // Global admins can view a company's plan but not change it.
  const readOnly = !!user?.isGlobalAdmin;
  const [plan, setPlan] = useState(null);
  // Billing point of contact (lives here on the My Plan page). Sourced from the
  // company settings loaded by the app shell and saved via the settings API.
  const [pocEmail, setPocEmail] = useState(settings.pointOfContactEmail || '');
  const [savingPoc, setSavingPoc] = useState(false);
  const [pocError, setPocError] = useState('');   // popup when a POC email is taken
  useEffect(() => { setPocEmail(settings.pointOfContactEmail || ''); }, [settings.pointOfContactEmail]);
  const savePoc = async () => {
    setSavingPoc(true);
    try {
      await api.updateSettings({ pointOfContactEmail: pocEmail });
      if (reloadSettings) await reloadSettings();
      showToast('Billing point of contact saved');
    } catch (e) {
      if (e.code === 'POC_EMAIL_TAKEN') setPocError(e.message);
      else showToast(e.message || 'Could not save billing point of contact', 'error');
    }
    setSavingPoc(false);
  };
  const [loadError, setLoadError] = useState('');
  const [addCount, setAddCount] = useState(1);
  const [removeCount, setRemoveCount] = useState(1);
  const [confirming, setConfirming] = useState(false);
  const [removeConfirming, setRemoveConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [subPayment, setSubPayment] = useState(null);   // { clientSecret } for the first invoice
  const [finalizing, setFinalizing] = useState(false);  // creating the subscription after payment method saved
  const [chosenPlan, setChosenPlan] = useState('monthly');
  const [chosenSeats, setChosenSeats] = useState('');

  const money = (n) => `$${Number(n || 0).toFixed(2)}`;

  const load = useCallback(async () => {
    try { setLoadError(''); setPlan(await api.getPlan()); }
    catch (e) { setLoadError(e.message); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Stripe.js instance, created once the publishable key is known
  const pubKey = plan && plan.stripePublishableKey;
  const stripePromise = useMemo(() => (pubKey ? loadStripe(pubKey) : null), [pubKey]);

  if (!plan) return (
    <>
      <div style={css.header}><div style={css.headerTitle}>My Plan</div></div>
      <div style={css.content}>
        {loadError
          ? <div style={{ ...css.card, color: theme.danger }}>{loadError}</div>
          : <p>Loading plan...</p>}
      </div>
    </>
  );

  const period = plan.planType === 'yearly' ? 'year' : 'month';
  const accessActive = plan.accessActive;       // can use the app (incl. ACH processing)
  const manageable = plan.subscriptionActive;   // active/trialing → can change seats
  const minSeats = Math.max(plan.usedSlots || 0, 1);
  const seatNum = parseInt(chosenSeats, 10) > 0 ? parseInt(chosenSeats, 10) : minSeats;
  // Price helper: base + per-seat for a given cycle.
  const priceFor = (planType, seats) => planType === 'yearly' ? 168 + 63 * seats : 20 + 7.5 * seats;
  // The plan must cover all active users → bill the greater of chosen vs active.
  const billedSeats = Math.max(seatNum, plan.usedSlots || 0, 1);
  const chosenPeriod = chosenPlan === 'yearly' ? 'year' : 'month';
  const chosenPrice = priceFor(chosenPlan, billedSeats);

  // Forecast (shown while on the free trial): what the plan will cost when the
  // trial converts. Uses the plan they've set up (if any), else the chooser
  // selection; and the greater of active users vs the chosen seat count.
  const trialingNow = plan.trialing;
  const forecastPlanType = trialingNow && plan.hasPaymentMethod
    ? (plan.subscriptionType || plan.planType || 'monthly')
    : chosenPlan;
  const forecastSeats = Math.max(
    plan.usedSlots || 0,
    (trialingNow && plan.hasPaymentMethod) ? (plan.userSlots || 0) : seatNum,
    1,
  );
  const forecastPeriod = forecastPlanType === 'yearly' ? 'year' : 'month';
  const forecastCost = priceFor(forecastPlanType, forecastSeats);

  const count = Math.max(1, parseInt(addCount, 10) || 1);
  const maxRemovable = plan.removableSlots || 0;
  const rcount = Math.max(1, parseInt(removeCount, 10) || 1);
  const removeValid = rcount <= maxRemovable;

  // Begin subscribing → creates a SetupIntent (no invoice yet) and opens the
  // payment dialog to collect a payment method.
  const startSubscribe = async () => {
    setSubscribing(true);
    try {
      const res = await api.subscribe(chosenPlan, seatNum);
      if (res.clientSecret) {
        setSubPayment({
          clientSecret: res.clientSecret,
          setupIntentId: res.setupIntentId,
          planType: res.planType || chosenPlan,
          seats: res.seats != null ? res.seats : seatNum,
          trialEnd: res.trialEnd || null,
          amountLabel: money(chosenPrice),
        });
      } else { await load(); showToast('Subscription created'); }
    } catch (e) { showToast(e.message, 'error'); }
    setSubscribing(false);
  };

  // The admin closed the dialog without confirming — nothing was created
  // (only an unconfirmed SetupIntent), so just close it.
  const cancelSubPayment = () => setSubPayment(null);

  // After the browser confirms the SetupIntent (payment method saved), create
  // the subscription. This is the first point an invoice is generated.
  const onSubscriptionPaid = async (status, setupIntent) => {
    const sp = subPayment;
    setSubPayment(null);
    setFinalizing(true);   // show the processing popup until it goes through
    try {
      const res = await api.finalizeSubscription({
        planType: sp && sp.planType,
        seats: sp && sp.seats,
        setupIntentId: (sp && sp.setupIntentId) || (setupIntent && setupIntent.id),
      });
      const ps = res.paymentStatus;
      if (ps === 'trial') {
        showToast('Payment method saved — you\'ll be charged when your free trial ends.');
        await load();
        return;
      }
      if (ps === 'processing') {
        showToast('Bank payment submitted — it may take a few business days to clear.');
        await load();
        return;
      }
      showToast('Subscription active');
      window.location.reload();   // refresh nav / billing popup / plan state
    } catch (e) {
      showToast(e.message || 'Could not finalize subscription', 'error');
      await load();
    } finally {
      setFinalizing(false);
    }
  };

  const confirmAdd = async () => {
    setBusy(true);
    try {
      const res = await api.addPlanSlots(count);
      setConfirming(false); setAddCount(1);
      if (res.plan) setPlan(res.plan);
      showToast(res.message || 'Seats added');
    } catch (e) { showToast(e.message, 'error'); }
    setBusy(false);
  };

  const confirmRemove = async () => {
    setBusy(true);
    try {
      const res = await api.removePlanSlots(rcount);
      setRemoveConfirming(false); setRemoveCount(1);
      if (res.plan) setPlan(res.plan);
      showToast(res.message || 'Seats removed');
    } catch (e) { showToast(e.message, 'error'); }
    setBusy(false);
  };

  const toggleAutoRenew = async () => {
    setBusy(true);
    try {
      const res = await api.setAutoRenew(!plan.autoRenew);
      if (res.plan) setPlan(res.plan);
      showToast(res.message || 'Renewal updated');
    } catch (e) { showToast(e.message, 'error'); }
    setBusy(false);
  };

  const trialing = plan.trialing;

  // Plan details are only shown for an active subscription (read live from
  // Stripe). Without one, fall back to neutral placeholders. During a free
  // trial, users are unlimited and there's no charge yet.
  const free = !!plan.freeAccess;   // complimentary permanent free tier
  const typeDisplay = free ? 'Complimentary' : trialing ? 'Free Trial' : (accessActive ? (plan.subscriptionType || plan.planType) : 'None');
  // On a trial we show the FORECAST cost (what they'll pay when it converts),
  // per the chosen billing cycle; otherwise the real period price.
  const costDisplay = free ? money(0) : trialing ? money(forecastCost) : (accessActive ? money(plan.periodPrice) : 'N/A');
  const statusDisplay = free ? 'Complimentary'
    : trialing ? 'Trial'
    : accessActive
      ? (plan.paymentProcessing || plan.subscriptionStatus === 'incomplete' ? 'Processing' : (plan.subscriptionStatus || 'active'))
      : 'Inactive';
  const seatsDisplay = (free || trialing) ? `${plan.usedSlots} / ∞` : (accessActive ? `${plan.usedSlots} / ${plan.userSlots}` : '—');
  const availableDisplay = (free || trialing) ? 'Unlimited' : (accessActive ? plan.availableSlots : 0);

  return (
    <>
      <div style={css.header}>
        <div style={css.headerTitle}>My Plan</div>
        <RefreshBtn onClick={load} />
      </div>
      <div style={css.content}>
        {/* ─── Billing Point of Contact ─── */}
        <div style={{ ...css.card, marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: theme.dark }}>Billing Point of Contact</h3>
          <p style={{ fontSize: 12, color: theme.gray, marginTop: 0, marginBottom: 16 }}>
            Required. Each company must have a unique billing contact email. The app is locked until one is set.
          </p>
          {readOnly ? (
            <div style={{ fontSize: 13, color: theme.dark, padding: '8px 12px', background: theme.lightGray, borderRadius: 6 }}>
              Billing point of contact: <strong>{settings.effectivePointOfContactEmail || '— not set —'}</strong>
            </div>
          ) : (
            <>
              {/* Pick a user as the billing POC (fills the email below) */}
              {(settings.pocCandidates && settings.pocCandidates.length > 0) && (
                <div style={{ marginBottom: 14 }}>
                  <label style={css.label}>Choose a user as the billing point of contact</label>
                  <select
                    style={{ ...css.select, width: '100%', maxWidth: 420 }}
                    value={(() => {
                      const cur = (pocEmail || '').toLowerCase().trim();
                      const match = settings.pocCandidates.find(u => (u.email || '').toLowerCase() === cur);
                      return match ? match.email : '';
                    })()}
                    onChange={e => setPocEmail(e.target.value)}>
                    <option value="">— Custom —</option>
                    {settings.pocCandidates.map(u => (
                      <option key={u.id} value={u.email}>{u.name} ({u.email})</option>
                    ))}
                  </select>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 260 }}>
                  <label style={css.label}>Billing Point of Contact Email</label>
                  <input style={css.input} type="email"
                    value={pocEmail}
                    placeholder="name@company.com"
                    onChange={e => setPocEmail(e.target.value)} />
                </div>
                <button style={css.btn('primary')} disabled={savingPoc} onClick={savePoc}>
                  {savingPoc ? 'Saving…' : 'Save Billing Point of Contact'}
                </button>
              </div>
              {settings.effectivePointOfContactEmail ? (
                <div style={{ fontSize: 13, color: theme.dark, marginTop: 12, padding: '8px 12px', background: theme.lightGray, borderRadius: 6 }}>
                  Billing point of contact in use: <strong>{settings.effectivePointOfContactEmail}</strong>
                </div>
              ) : (
                <div style={{ fontSize: 13, color: theme.danger, marginTop: 12, padding: '8px 12px', background: '#FEF2F2', border: `1px solid ${theme.danger}`, borderRadius: 6, fontWeight: 600 }}>
                  Not set — this company is locked until a billing point of contact is saved.
                </div>
              )}
            </>
          )}
        </div>

        {!plan.stripeEnabled && (
          <div style={{ ...css.card, color: theme.danger, fontSize: 13 }}>
            Billing isn't configured on the server (no Stripe key set). Subscriptions are unavailable until STRIPE_SECRET_KEY / STRIPE_PUBLISHABLE_KEY are provided.
          </div>
        )}
        {pocError && (
          <div style={css.modal} onClick={() => setPocError('')}>
            <div style={{ ...css.modalContent, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
              <h2 style={{ margin: '0 0 12px', fontSize: 17, fontWeight: 700, color: theme.dark }}>Email already in use</h2>
              <p style={{ fontSize: 14, color: theme.gray, marginTop: 0 }}>{pocError}</p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                <button style={css.btn('primary')} onClick={() => setPocError('')}>OK</button>
              </div>
            </div>
          </div>
        )}

        {/* Complimentary free-tier banner */}
        {free && (
          <div style={{ ...css.card, background: '#F0FDFA', border: `1px solid ${theme.primary}` }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: theme.dark, marginBottom: 4 }}>
              Complimentary access — unlimited users
            </div>
            <div style={{ fontSize: 13, color: theme.gray }}>
              This company has a complimentary free tier: full access with no charge and no user limit. No subscription or payment method is required.
            </div>
          </div>
        )}

        {/* Free-trial banner */}
        {!free && trialing && (
          <div style={{ ...css.card, background: '#F0FDFA', border: `1px solid ${theme.primary}` }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: theme.dark, marginBottom: 4 }}>
              Free trial — unlimited users
            </div>
            <div style={{ fontSize: 13, color: theme.gray }}>
              Your trial ends on <strong>{formatDate(plan.trialEndsAt)}</strong>. Add as many users as you like until then.
              {plan.hasPaymentMethod
                ? ' A payment method is saved — your plan begins automatically when the trial ends.'
                : ' Set up a plan below to continue seamlessly (you won\'t be charged until the trial ends).'}
            </div>
          </div>
        )}

        {/* Global admin: read-only notice */}
        {readOnly && (
          <div style={{ ...css.card, background: '#F0FDFA', border: `1px solid ${theme.primary}`, fontSize: 13, color: theme.gray }}>
            <strong style={{ color: theme.dark }}>Read-only view.</strong> As a global admin you can review this company's plan, but starting or changing a subscription must be done by the company's own admin.
          </div>
        )}

        {/* No active subscription, read-only (global admin can't start one) */}
        {plan.stripeEnabled && !accessActive && readOnly && (
          <div style={{ ...css.card, background: '#FFFBEB', border: `1px solid #FDE68A`, fontSize: 13, color: theme.gray }}>
            This company has no active subscription. A company admin must start one.
          </div>
        )}

        {/* Subscription status / start — choose a plan. Also shown during a
            trial (no subscription yet) to set up payment in advance. */}
        {plan.stripeEnabled && !readOnly && (!accessActive || (trialing && !plan.hasPaymentMethod)) && (
          <div style={{ ...css.card, background: '#FFFBEB', border: `1px solid #FDE68A` }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: theme.dark, marginBottom: 4 }}>
              {trialing
                ? 'Set up your plan for after the trial'
                : (plan.subscriptionStatus && plan.subscriptionStatus !== 'incomplete'
                  ? `Subscription ${plan.subscriptionStatus} — choose a plan to restart billing`
                  : 'Choose a plan to get started')}
            </div>
            <div style={{ fontSize: 13, color: theme.gray, marginBottom: 14 }}>
              {trialing
                ? 'Pick a billing cycle and save a payment method now. You won\'t be charged until your free trial ends.'
                : 'Pick a billing cycle, then pay by card or US bank account. Renewals and proration are handled automatically.'}
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
              {[
                { key: 'monthly', title: 'Monthly', desc: '$20 + $7.50 per seat / month' },
                { key: 'yearly', title: 'Yearly', desc: '$168 + $63 per seat / year — 30% discounted from monthly price' },
              ].map(opt => (
                <div key={opt.key} onClick={() => setChosenPlan(opt.key)}
                  style={{
                    flex: 1, minWidth: 200, cursor: 'pointer', borderRadius: 8, padding: '14px 16px',
                    border: `2px solid ${chosenPlan === opt.key ? theme.primary : theme.border}`,
                    background: chosenPlan === opt.key ? 'rgba(0,180,185,0.06)' : theme.white,
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      width: 14, height: 14, borderRadius: '50%', display: 'inline-block',
                      border: `4px solid ${chosenPlan === opt.key ? theme.primary : theme.border}`,
                    }} />
                    <span style={{ fontWeight: 700, color: theme.dark }}>{opt.title}</span>
                  </div>
                  <div style={{ fontSize: 13, color: theme.gray, marginTop: 6 }}>{opt.desc}</div>
                </div>
              ))}
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={css.label}>Seats</label>
              <input type="number" min={minSeats} style={{ ...css.input, width: 140 }}
                value={chosenSeats} placeholder={String(minSeats)}
                onChange={e => setChosenSeats(e.target.value)} />
              <span style={{ fontSize: 12, color: theme.gray, marginLeft: 10 }}>
                Must cover all {plan.usedSlots} active user{plan.usedSlots !== 1 ? 's' : ''} — minimum {minSeats} seat{minSeats !== 1 ? 's' : ''}.
              </span>
              <div style={{ fontSize: 13, color: theme.dark, marginTop: 10 }}>
                {seatNum} seat{seatNum !== 1 ? 's' : ''} → <strong>{money(chosenPrice)}</strong> / {chosenPeriod}
                {trialing ? ' (charged when your trial ends)' : ''}.
              </div>
              {(plan.usedSlots || 0) > 1 && (
                <div style={{ fontSize: 12, color: theme.gray, marginTop: 6, fontStyle: 'italic' }}>
                  Want a cheaper plan? Deactivate users on the Users page first — the plan can't have fewer seats than your active users.
                </div>
              )}
            </div>
            <button style={css.btn('primary')} disabled={subscribing} onClick={startSubscribe}>
              {subscribing ? 'Starting…'
                : trialing
                  ? `Set up ${chosenPlan} plan — ${seatNum} seat${seatNum !== 1 ? 's' : ''} (charged after trial)`
                  : `Start ${chosenPlan} subscription — ${seatNum} seat${seatNum !== 1 ? 's' : ''}`}
            </button>
          </div>
        )}

        {/* Summary */}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
          <div style={css.statCard(theme.primary)}>
            <div style={{ fontSize: 12, color: theme.gray, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Plan</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: theme.dark, textTransform: 'capitalize' }}>{typeDisplay}</div>
          </div>
          <div style={css.statCard(theme.dark)}>
            <div style={{ fontSize: 12, color: theme.gray, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{trialing ? `Forecast / ${forecastPeriod}` : `Cost${accessActive ? ` / ${period}` : ''}`}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: theme.dark }}>{costDisplay}</div>
          </div>
          <div style={css.statCard(theme.warning)}>
            <div style={{ fontSize: 12, color: theme.gray, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: theme.dark, textTransform: 'capitalize' }}>{statusDisplay}</div>
            {trialing ? (
              <div style={{ fontSize: 12, color: theme.gray, marginTop: 2 }}>trial ends {formatDate(plan.trialEndsAt)}</div>
            ) : manageable && plan.currentPeriodEnd && (
              <div style={{ fontSize: 12, color: plan.cancelAtPeriodEnd ? theme.danger : theme.gray, marginTop: 2 }}>
                {plan.cancelAtPeriodEnd ? 'ends' : 'renews'} {formatDate(plan.currentPeriodEnd)}
              </div>
            )}
          </div>
          <div style={css.statCard(theme.success)}>
            <div style={{ fontSize: 12, color: theme.gray, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Seats</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: theme.dark }}>{seatsDisplay}</div>
            <div style={{ fontSize: 12, color: theme.gray, marginTop: 2 }}>{availableDisplay} available</div>
          </div>
        </div>

        {accessActive && !trialing && (
          <div style={{ ...css.card, fontSize: 13, color: theme.gray }}>
            {money(plan.basePrice)} + {money(plan.perUserPeriodPrice)} per seat / {period}
            {plan.planType === 'yearly' ? ' (30% discount from monthly price)' : ''}. Renewals and proration are handled automatically by Stripe.
          </div>
        )}

        {manageable && !readOnly && !trialing && (
          <>
            {/* Add seats */}
            <div style={css.card}>
              <div style={{ fontSize: 16, fontWeight: 700, color: theme.dark, marginBottom: 4 }}>Add user slots</div>
              <div style={{ fontSize: 13, color: theme.gray, marginBottom: 16 }}>
                Stripe prorates the change and charges your card on file immediately. Your {period}ly price rises by {money(plan.perUserPeriodPrice)} per seat.
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <label style={css.label}>Seats to add</label>
                  <input type="number" min="1" style={{ ...css.input, width: 120 }} value={addCount}
                    onChange={e => setAddCount(e.target.value)} />
                </div>
                <button style={css.btn('primary')} onClick={() => setConfirming(true)}>Add {count} seat{count !== 1 ? 's' : ''}</button>
              </div>
            </div>

            {/* Remove seats */}
            <div style={css.card}>
              <div style={{ fontSize: 16, fontWeight: 700, color: theme.dark, marginBottom: 4 }}>Remove user slots</div>
              <div style={{ fontSize: 13, color: theme.gray, marginBottom: 16 }}>
                You can remove down to your current user count ({plan.usedSlots} user{plan.usedSlots !== 1 ? 's' : ''}). Stripe applies a prorated credit to your next invoice. Up to <strong>{maxRemovable}</strong> removable.
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <label style={css.label}>Seats to remove</label>
                  <input type="number" min="1" style={{ ...css.input, width: 120 }} value={removeCount}
                    onChange={e => setRemoveCount(e.target.value)} />
                </div>
                <button
                  style={{ ...css.btn('danger'), opacity: (maxRemovable < 1 || !removeValid) ? 0.5 : 1, cursor: (maxRemovable < 1 || !removeValid) ? 'not-allowed' : 'pointer' }}
                  disabled={maxRemovable < 1 || !removeValid}
                  onClick={() => setRemoveConfirming(true)}>
                  Remove {rcount} seat{rcount !== 1 ? 's' : ''}
                </button>
                {!removeValid && maxRemovable >= 1 && (
                  <span style={{ fontSize: 12, color: theme.danger }}>Only {maxRemovable} removable — remove users first.</span>
                )}
                {maxRemovable < 1 && (
                  <span style={{ fontSize: 12, color: theme.gray }}>No removable seats — every seat is in use.</span>
                )}
              </div>
            </div>
          </>
        )}

        {/* Automatic renewal. Shown for active subscriptions, and during a trial
            once a payment method is set up (so the pending renewal can be
            cancelled). Global admins see status only. */}
        {manageable && (!trialing || plan.hasPaymentMethod) && (
            <div style={css.card}>
              <div style={{ fontSize: 16, fontWeight: 700, color: theme.dark, marginBottom: 4 }}>
                {trialing ? 'Renewal after trial' : 'Automatic renewal'}
              </div>
              <div style={{ fontSize: 13, color: theme.gray, marginBottom: 16 }}>
                {trialing
                  ? (plan.autoRenew
                    ? `When your trial ends on ${plan.trialEndsAt ? formatDate(plan.trialEndsAt) : 'the trial end'}, your plan starts and renews automatically. Cancel renewal to end after the trial with no charge.`
                    : `Renewal is cancelled — your plan will end when the trial ends on ${plan.trialEndsAt ? formatDate(plan.trialEndsAt) : 'the trial end'} and you won't be charged. Turn it back on to continue after the trial.`)
                  : (plan.autoRenew
                    ? `Your subscription renews automatically on ${plan.currentPeriodEnd ? formatDate(plan.currentPeriodEnd) : 'the period end'}. Turn this off to let it end after the current period.`
                    : `Automatic renewal is off. Your subscription stays active until ${plan.currentPeriodEnd ? formatDate(plan.currentPeriodEnd) : 'the period end'}, then cancels. Turn it back on to keep it.`)}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={css.badge(plan.autoRenew ? theme.success : theme.warning)}>
                  {plan.autoRenew ? 'On' : 'Off'}
                </span>
                {!readOnly && (
                  <button
                    style={css.btn(plan.autoRenew ? 'danger' : 'primary')}
                    disabled={busy}
                    onClick={toggleAutoRenew}>
                    {busy
                      ? 'Processing…'
                      : trialing
                        ? (plan.autoRenew ? 'Cancel renewal' : 'Enable renewal')
                        : (plan.autoRenew ? 'Disable automatic renewal' : 'Enable automatic renewal')}
                  </button>
                )}
              </div>
            </div>
        )}

        {/* Billing history (Stripe invoices) */}
        {plan.invoices && plan.invoices.length > 0 && (
          <div style={css.card}>
            <div style={{ fontSize: 16, fontWeight: 700, color: theme.dark, marginBottom: 12 }}>Billing history</div>
            <table style={css.table}>
              <thead><tr>
                <th style={css.th}>Date</th><th style={css.th}>Invoice</th>
                <th style={css.th}>Amount</th><th style={css.th}>Status</th>
              </tr></thead>
              <tbody>
                {plan.invoices.map(inv => (
                  <tr key={inv.id}>
                    <td style={css.td}>{formatDate(inv.created)}</td>
                    <td style={css.td}>
                      {inv.hostedUrl
                        ? <a href={inv.hostedUrl} target="_blank" rel="noreferrer" style={{ color: theme.primary }}>{inv.number || inv.id}</a>
                        : (inv.number || inv.id)}
                    </td>
                    <td style={css.td}>{money(inv.amount)}</td>
                    <td style={css.td}><span style={css.badge(inv.status === 'paid' ? theme.success : inv.status === 'open' ? theme.warning : theme.gray)}>{inv.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Start subscription — card or bank (Stripe Elements / PaymentElement) */}
      {subPayment && stripePromise && (
        <div style={css.modal} onClick={cancelSubPayment}>
          <div style={{ ...css.modalContent, maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 18, fontWeight: 700, color: theme.dark, marginBottom: 16 }}>
              {subPayment.trialEnd ? 'Set up payment for after your trial' : 'Start subscription'}
            </div>
            <Elements stripe={stripePromise} options={{ clientSecret: subPayment.clientSecret }}>
              <PaymentForm
                amountLabel={subPayment.amountLabel}
                trialEndLabel={subPayment.trialEnd ? formatDate(subPayment.trialEnd) : null}
                onCancel={cancelSubPayment}
                onPaid={onSubscriptionPaid}
              />
            </Elements>
          </div>
        </div>
      )}

      {/* Finalizing payment — processing popup (non-dismissable) */}
      {finalizing && (
        <div style={css.modal}>
          <div style={{ ...css.modalContent, maxWidth: 360, textAlign: 'center' }}>
            <svg width="44" height="44" viewBox="0 0 50 50" style={{ display: 'block', margin: '0 auto 16px' }}>
              <circle cx="25" cy="25" r="20" fill="none" stroke={theme.border} strokeWidth="5" />
              <path d="M25 5 a20 20 0 0 1 20 20" fill="none" stroke={theme.primary} strokeWidth="5" strokeLinecap="round">
                <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="0.9s" repeatCount="indefinite" />
              </path>
            </svg>
            <div style={{ fontSize: 16, fontWeight: 700, color: theme.dark, marginBottom: 6 }}>Processing your payment…</div>
            <div style={{ fontSize: 13, color: theme.gray }}>Setting up your subscription. Please don't close this window.</div>
          </div>
        </div>
      )}

      {/* Add seats confirm modal */}
      {confirming && (
        <div style={css.modal} onClick={() => !busy && setConfirming(false)}>
          <div style={{ ...css.modalContent, maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 18, fontWeight: 700, color: theme.dark, marginBottom: 12 }}>Add user slots</div>
            <div style={{ fontSize: 14, color: theme.dark, marginBottom: 8 }}>
              Add <strong>{count}</strong> seat{count !== 1 ? 's' : ''} to {plan.companyName}?
            </div>
            <div style={{ fontSize: 13, color: theme.gray, marginBottom: 20 }}>
              Stripe will charge the prorated amount to your card on file now, and your {period}ly price becomes <strong>{money(plan.periodPrice + plan.perUserPeriodPrice * count)}</strong>.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button style={css.btn('outline')} disabled={busy} onClick={() => setConfirming(false)}>Cancel</button>
              <button style={css.btn('primary')} disabled={busy} onClick={confirmAdd}>{busy ? 'Processing…' : 'Add seats'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Remove seats confirm modal */}
      {removeConfirming && (
        <div style={css.modal} onClick={() => !busy && setRemoveConfirming(false)}>
          <div style={{ ...css.modalContent, maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 18, fontWeight: 700, color: theme.dark, marginBottom: 12 }}>Remove user slots</div>
            <div style={{ fontSize: 14, color: theme.dark, marginBottom: 8 }}>
              Remove <strong>{rcount}</strong> seat{rcount !== 1 ? 's' : ''} from {plan.companyName}?
            </div>
            <div style={{ fontSize: 13, color: theme.gray, marginBottom: 20 }}>
              Stripe applies a prorated credit to your next invoice. Your {period}ly price becomes <strong>{money(Math.max(plan.basePrice, plan.periodPrice - plan.perUserPeriodPrice * rcount))}</strong>.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button style={css.btn('outline')} disabled={busy} onClick={() => setRemoveConfirming(false)}>Cancel</button>
              <button style={css.btn('danger')} disabled={busy} onClick={confirmRemove}>{busy ? 'Processing…' : `Remove ${rcount} seat${rcount !== 1 ? 's' : ''}`}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   CHANGE PASSWORD PAGE
   ═══════════════════════════════════════════════════════════════════ */
function ChangePasswordPage({ showToast }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { showToast('Password must be at least 6 characters', 'error'); return; }
    if (newPassword !== confirmPassword) { showToast('Passwords do not match', 'error'); return; }
    setSaving(true);
    try {
      await api.changePassword(currentPassword, newPassword);
      showToast('Password updated successfully');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) {
      showToast(err.message, 'error');
    }
    setSaving(false);
  };

  return (
    <>
      <div style={css.header}>
        <div style={css.headerTitle}>Change Password</div>
      </div>
      <div style={css.content}>
        <div style={{ ...css.card, maxWidth: 480 }}>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 16 }}>
              <label style={css.label}>Current Password</label>
              <input style={css.input} type="password" value={currentPassword} required
                onChange={e => setCurrentPassword(e.target.value)} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={css.label}>New Password</label>
              <input style={css.input} type="password" value={newPassword} required
                onChange={e => setNewPassword(e.target.value)} placeholder="Min 6 characters" />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={css.label}>Confirm New Password</label>
              <input style={css.input} type="password" value={confirmPassword} required
                onChange={e => setConfirmPassword(e.target.value)} />
              {newPassword && confirmPassword && (
                <div style={{ fontSize: 12, marginTop: 4, color: newPassword === confirmPassword ? theme.success : theme.danger }}>
                  {newPassword === confirmPassword ? 'Passwords match' : 'Passwords do not match'}
                </div>
              )}
            </div>
            <button type="submit" disabled={saving} style={css.btn('primary')}>
              {saving ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        </div>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   USERS PAGE (Admin / Manager only)
   ═══════════════════════════════════════════════════════════════════ */
const ROLES = [
  { value: 'ADMIN', label: 'Admin', color: '#F59E0B' },
  { value: 'MANAGER', label: 'Manager', color: '#3B82F6' },
  { value: 'USER', label: 'User', color: '#6B7280' },
];
const ROLE_MAP = Object.fromEntries(ROLES.map(r => [r.value, r]));

function UsersPage({ showToast, onGoToPlan }) {
  const { isAdmin, user: currentUser, activeCompanyId } = useAuth();
  // Global admin "all" view: read-only cross-company list of admins.
  const globalView = activeCompanyId === 'all';
  const [capacityMsg, setCapacityMsg] = useState('');
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [hideInactive, setHideInactive] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [form, setForm] = useState({ email: '', password: '', firstName: '', lastName: '', role: 'USER', phone: '', isFranchisee: false });
  const [capacity, setCapacity] = useState(null);

  const loadUsers = useCallback(async () => {
    try {
      const res = await api.getUsers({ limit: 100, isActive: hideInactive ? 'true' : undefined });
      setUsers(res.data || []);
      setCapacity(res.capacity || null);
    } catch (e) { showToast(e.message, 'error'); }
  }, [hideInactive, showToast]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const openCreate = () => {
    setForm({ email: '', password: 'changeme123', firstName: '', lastName: '', role: 'USER', phone: '', isFranchisee: false });
    setEditing(null);
    setShowForm(true);
  };

  // Clicking "New User" at capacity shows the plan popup instead of the form
  const handleNewUser = () => {
    if (capacity && !capacity.unlimited && capacity.available <= 0) {
      setCapacityMsg(`User capacity reached (${capacity.used}/${capacity.slots} active). Add more user slots on the My Plan page, or remove/deactivate a user to free a seat.`);
    } else {
      openCreate();
    }
  };

  const openEdit = (u) => {
    setForm({
      email: u.email, firstName: u.firstName, lastName: u.lastName,
      role: u.role, phone: u.phone || '', isActive: u.isActive, isFranchisee: !!u.isFranchisee,
    });
    setEditing(u.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.updateUser(editing, form);
        showToast('User updated');
      } else {
        const result = await api.createUser(form);
        showToast(result?.message || 'User created');
      }
      setShowForm(false);
      loadUsers();
    } catch (err) {
      if (err.code === 'CAPACITY_FULL') { setShowForm(false); setCapacityMsg(err.message); }
      else showToast(err.message, 'error');
    }
  };

  const handleDelete = async (userId) => {
    try {
      const r = await api.deleteUser(userId);
      showToast(r?.message || 'User deleted');
      setDeleteConfirm(null);
      loadUsers();
    } catch (err) { showToast(err.message, 'error'); setDeleteConfirm(null); }
  };

  // Quick activate/deactivate toggle for the user's status in this company.
  const toggleActive = async (u) => {
    try {
      await api.updateUser(u.id, { isActive: !u.isActive });
      showToast(u.isActive ? `${u.firstName} ${u.lastName} deactivated` : `${u.firstName} ${u.lastName} activated`);
      loadUsers();
    } catch (err) {
      // Reactivating can exceed a paid plan's capacity → show the plan popup.
      if (err.code === 'CAPACITY_FULL') setCapacityMsg(err.message);
      else showToast(err.message, 'error');
    }
  };

  const handleResetPassword = async () => {
    if (!resetTarget || !resetPassword.trim()) return;
    try {
      await api.resetUserPassword(resetTarget.id, resetPassword);
      showToast(`Password reset for ${resetTarget.firstName} ${resetTarget.lastName}`);
      setResetTarget(null);
      setResetPassword('');
    } catch (err) { showToast(err.message, 'error'); }
  };

  return (
    <>
      <div style={css.header}>
        <div style={css.headerTitle}>{globalView ? 'Company Admins' : 'User Management'}</div>
        {!globalView && (
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            {capacity && (
              capacity.unlimited
                ? <span style={css.badge(theme.primary)}>{capacity.used} users · Unlimited (trial)</span>
                : <span style={css.badge(capacity.available > 0 ? theme.success : theme.danger)}>
                    {capacity.used} / {capacity.slots} seats used
                  </span>
            )}
            <label style={{ fontSize: 13, color: theme.gray, display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={hideInactive} onChange={e => setHideInactive(e.target.checked)} />
              Hide inactive
            </label>
            <button style={css.btn('primary')} onClick={handleNewUser}>+ New User</button>
          </div>
        )}
      </div>
      {!globalView && capacity && !capacity.unlimited && capacity.available <= 0 && (
        <div style={{ background: '#FEF2F2', color: theme.danger, padding: '10px 32px', fontSize: 13, fontWeight: 500 }}>
          You've reached your plan capacity ({capacity.used}/{capacity.slots} seats). Add more user slots on the My Plan page, or remove/transfer a user to free a seat.
        </div>
      )}
      <div style={css.content}>
        <div style={css.card}>
          <table style={css.table}>
            <thead>
              {globalView ? (
                <tr>
                  <th style={css.th}>Name</th>
                  <th style={css.th}>Email</th>
                  <th style={css.th}>Admin of</th>
                  <th style={css.th}>Last Login</th>
                </tr>
              ) : (
                <tr>
                  <th style={css.th}>Name</th>
                  <th style={css.th}>Email</th>
                  <th style={css.th}>Role</th>
                  <th style={css.th}>Franchisee</th>
                  <th style={css.th}>Status</th>
                  <th style={css.th}>Last Login</th>
                  <th style={css.th}>Actions</th>
                </tr>
              )}
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr><td colSpan={globalView ? 4 : 7} style={{ ...css.td, textAlign: 'center', color: theme.gray }}>No {globalView ? 'admins' : 'users'} found.</td></tr>
              ) : users.map(u => {
                const role = ROLE_MAP[u.role] || { label: u.role, color: '#999' };
                if (globalView) {
                  return (
                    <tr key={u.id}>
                      <td style={{ ...css.td, fontWeight: 600 }}>
                        {u.firstName} {u.lastName}
                        {u.id === currentUser.id && <span style={{ fontSize: 10, color: theme.primary, marginLeft: 6 }}>(you)</span>}
                      </td>
                      <td style={{ ...css.td, fontSize: 13 }}>{u.email}</td>
                      <td style={{ ...css.td, fontSize: 13 }}>
                        {(u.adminCompanies && u.adminCompanies.length)
                          ? u.adminCompanies.map(name => (
                              <span key={name} style={{ ...css.badge(theme.primary), marginRight: 4 }}>{name}</span>
                            ))
                          : <span style={{ color: theme.gray }}>—</span>}
                      </td>
                      <td style={{ ...css.td, fontSize: 12, color: theme.gray }}>
                        {u.lastLoginAt ? formatDateTime(u.lastLoginAt) : 'Never'}
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={u.id}>
                    <td style={{ ...css.td, fontWeight: 600 }}>
                      {u.firstName} {u.lastName}
                      {u.id === currentUser.id && <span style={{ fontSize: 10, color: theme.primary, marginLeft: 6 }}>(you)</span>}
                    </td>
                    <td style={{ ...css.td, fontSize: 13 }}>{u.email}</td>
                    <td style={css.td}><span style={css.badge(role.color)}>{role.label}</span></td>
                    <td style={css.td}>
                      {u.isFranchisee
                        ? <span style={css.badge(theme.primary)}>Franchisee</span>
                        : <span style={{ color: theme.gray }}>—</span>}
                    </td>
                    <td style={css.td}>
                      <span style={css.badge(u.isActive ? theme.success : theme.danger)}>
                        {u.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ ...css.td, fontSize: 12, color: theme.gray }}>
                      {u.lastLoginAt ? formatDateTime(u.lastLoginAt) : 'Never'}
                    </td>
                    <td style={css.td}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {isAdmin && (
                          <button style={{ ...css.btn('outline'), padding: '4px 10px', fontSize: 12 }} onClick={() => openEdit(u)}>
                            Edit
                          </button>
                        )}
                        {isAdmin && u.id !== currentUser.id && (
                          <button
                            style={{ ...css.btn(u.isActive ? 'outline' : 'primary'), padding: '4px 10px', fontSize: 12 }}
                            onClick={() => toggleActive(u)}>
                            {u.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                        )}
                        <button style={{ ...css.btn('outline'), padding: '4px 10px', fontSize: 12 }}
                          onClick={() => { setResetTarget(u); setResetPassword(''); }}>
                          Reset PW
                        </button>
                        {u.id !== currentUser.id && (
                          <button style={{ ...css.btn('danger'), padding: '4px 10px', fontSize: 12 }}
                            onClick={() => setDeleteConfirm(u)}>
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create / Edit User Modal */}
      {showForm && (
        <div style={css.modal} onClick={() => setShowForm(false)}>
          <div style={css.modalContent} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 24px', fontSize: 18, fontWeight: 700, color: theme.dark }}>
              {editing ? 'Edit User' : 'Create User'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                <div style={{ marginBottom: 14 }}>
                  <label style={css.label}>First Name *</label>
                  <input style={css.input} value={form.firstName} required
                    onChange={e => setForm({ ...form, firstName: e.target.value })} />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={css.label}>Last Name *</label>
                  <input style={css.input} value={form.lastName} required
                    onChange={e => setForm({ ...form, lastName: e.target.value })} />
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={css.label}>Email *</label>
                <input style={css.input} type="email" value={form.email} required
                  onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>
              {!editing && (
                <div style={{ marginBottom: 14 }}>
                  <label style={css.label}>Password</label>
                  <input style={css.input} value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })} />
                  <div style={{ fontSize: 11, color: theme.gray, marginTop: 4 }}>Default: changeme123</div>
                </div>
              )}
              <div style={{ marginBottom: 14 }}>
                <label style={css.label}>Phone</label>
                <input style={css.input} type="tel" value={form.phone}
                  onChange={e => setForm({ ...form, phone: e.target.value })} />
              </div>
              {isAdmin && (
                <div style={{ marginBottom: 14 }}>
                  <label style={css.label}>Role</label>
                  <select style={{ ...css.select, width: '100%' }} value={form.role}
                    onChange={e => setForm({ ...form, role: e.target.value })}>
                    {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
              )}
              {isAdmin && (
                <div style={{ marginBottom: 14 }}>
                  <label style={{ ...css.label, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" checked={!!form.isFranchisee}
                      onChange={e => setForm({ ...form, isFranchisee: e.target.checked })} />
                    Franchisee
                  </label>
                  <div style={{ fontSize: 11, color: theme.gray, marginTop: 2 }}>
                    Separate from role. Only franchisees can complete the DocuSign step in the pipeline.
                  </div>
                </div>
              )}
              {editing && isAdmin && (
                <div style={{ marginBottom: 14 }}>
                  <label style={{ ...css.label, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" checked={form.isActive !== false}
                      onChange={e => setForm({ ...form, isActive: e.target.checked })} />
                    Active
                  </label>
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
                <button type="button" style={css.btn('outline')} onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" style={css.btn('primary')}>
                  {editing ? 'Save Changes' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div style={css.modal} onClick={() => setDeleteConfirm(null)}>
          <div style={{ ...css.modalContent, maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 700, color: theme.danger }}>
              Delete User
            </h2>
            <p style={{ fontSize: 14, color: theme.dark, marginBottom: 20 }}>
              Are you sure you want to delete <strong>{deleteConfirm.firstName} {deleteConfirm.lastName}</strong> ({deleteConfirm.email})?
              This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button style={css.btn('outline')} onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button style={css.btn('danger')} onClick={() => handleDelete(deleteConfirm.id)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetTarget && (
        <div style={css.modal} onClick={() => setResetTarget(null)}>
          <div style={{ ...css.modalContent, maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 700, color: theme.dark }}>
              Reset Password
            </h2>
            <p style={{ fontSize: 14, color: theme.gray, marginBottom: 16 }}>
              Set a new password for <strong>{resetTarget.firstName} {resetTarget.lastName}</strong>.
            </p>
            <div style={{ marginBottom: 16 }}>
              <label style={css.label}>New Password</label>
              <input style={css.input} type="text" value={resetPassword}
                onChange={e => setResetPassword(e.target.value)} placeholder="New password" />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button style={css.btn('outline')} onClick={() => setResetTarget(null)}>Cancel</button>
              <button style={css.btn('primary')} onClick={handleResetPassword} disabled={!resetPassword.trim()}>
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Capacity-reached popup */}
      {capacityMsg && (
        <div style={css.modal} onClick={() => setCapacityMsg('')}>
          <div style={{ ...css.modalContent, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 700, color: theme.dark }}>
              Plan capacity reached
            </h2>
            <p style={{ fontSize: 14, color: theme.gray, marginBottom: 20 }}>{capacityMsg}</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button style={css.btn('outline')} onClick={() => setCapacityMsg('')}>Close</button>
              {isAdmin && onGoToPlan && (
                <button style={css.btn('primary')} onClick={() => { setCapacityMsg(''); onGoToPlan(); }}>
                  Go to My Plan
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
